import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { cvAPI, formsAPI, interviewsAPI, cvScreeningAPI, assessmentsAPI } from '../api/client';
import { useAuth } from '../context/AuthContext';
import Modal from '../components/Common/Modal';
import RemoteAssessmentPanel from '../components/HR/RemoteAssessmentPanel';
import { buildFormWorkflow, stageLabels } from '../components/HR/formWorkflow';
import {
  UserGroupIcon, BriefcaseIcon, EnvelopeIcon, ClipboardDocumentListIcon,
  PlusIcon, PencilSquareIcon, TrashIcon, CheckCircleIcon,
  XCircleIcon, ArrowPathIcon, ArrowDownTrayIcon, PaperAirplaneIcon,
  CalendarDaysIcon, ShieldExclamationIcon, MagnifyingGlassIcon,
  ExclamationCircleIcon, ArrowTopRightOnSquareIcon, PhoneIcon, ChevronRightIcon,
  AcademicCapIcon, ClipboardIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const TABS = [
  { key: 'positions',  label: 'Positions',       icon: BriefcaseIcon },
  { key: 'templates',  label: 'Email Templates', icon: EnvelopeIcon },
  { key: 'candidates', label: 'Candidates',      icon: UserGroupIcon },
  { key: 'review',     label: 'Form Review',     icon: ShieldExclamationIcon },
  { key: 'exams', label: 'Exam Tracking', icon: AcademicCapIcon },
  { key: 'followups',  label: 'Follow-ups',      icon: PhoneIcon },
  { key: 'forms',      label: 'Form Responses',  icon: ClipboardDocumentListIcon },
];

// Form-screening status badge (separate from the candidate pipeline status)
const FORM_SCREENING_BADGE = {
  passed:           { label: '✅ Passed',          color: 'bg-green-100 text-green-700' },
  held:             { label: '🟡 On Hold',         color: 'bg-amber-100 text-amber-700' },
  review_needed:    { label: '⚠️ Needs Review',    color: 'bg-orange-100 text-orange-700' },
  pending_hr_review:{ label: '🔴 No Form Questions', color: 'bg-red-100 text-red-700' },
};

// Per-field result indicators inside the review tables
const FIELD_STATUS_META = {
  pass:   { icon: '✅', color: 'text-green-600' },
  hold:   { icon: '🟡', color: 'text-amber-600' },
  review: { icon: '⚠️', color: 'text-orange-600' },
};

const TIER_META = {
  interview:       { label: 'Interview Call',       color: 'bg-green-50 text-green-700 border-green-200',    range: 'Score ≥ 80%'                           },
  details:         { label: 'Request More Details', color: 'bg-amber-50 text-amber-700 border-amber-200',    range: 'Score 50–79%'                          },
  rejection:       { label: 'Rejection',            color: 'bg-red-50 text-red-700 border-red-200',          range: 'Score < 50%'                           },
  form_invite:     { label: 'Form Invitation',      color: 'bg-purple-50 text-purple-700 border-purple-200', range: 'Auto-sent to interview candidates'      },
  schedule_invite: { label: 'Interview Scheduling', color: 'bg-cyan-50 text-cyan-700 border-cyan-200',       range: 'Auto-sent after form submission'        },
};

const STATUS_COLORS = {
  pending:              'bg-gray-100 text-gray-600',
  screening:            'bg-yellow-100 text-yellow-700',
  passed:               'bg-green-100 text-green-700',
  failed:               'bg-red-100 text-red-700',
  interview_invited:    'bg-green-100 text-green-700',
  details_requested:    'bg-amber-100 text-amber-700',
  rejected:             'bg-red-100 text-red-700',
  form_sent:            'bg-blue-100 text-blue-700',
  form_submitted:       'bg-purple-100 text-purple-700',
  screened_pending_review: 'bg-indigo-100 text-indigo-700',
  // Never scored — the CV could not be read. Deliberately not red: this is not
  // a rejection and must not read like one.
  unreadable_cv:        'bg-orange-100 text-orange-700',
  schedule_sent:        'bg-cyan-100 text-cyan-700',
  interview_scheduled:  'bg-violet-100 text-violet-700',
  interviewed:          'bg-indigo-100 text-indigo-700',
  // HR called and closed them out. Terminal, but not red for the same reason
  // unreadable_cv is not: this says nothing about the person, only that they
  // never came back. Slate rather than the gray `pending` uses, so "we stopped
  // chasing" does not read as "not started yet" at a glance.
  no_response:          'bg-slate-200 text-slate-700',
};

const EMAIL_TYPE_BADGE = {
  none:      { label: '—',            color: 'text-gray-400' },
  interview: { label: '✉ Interview',  color: 'text-green-600' },
  details:   { label: '✉ Details',    color: 'text-amber-600' },
  rejection: { label: '✉ Rejection',  color: 'text-red-500'   },
};


export default function CvScreening() {
  const { departmentId } = useParams();
  const deptId = parseInt(departmentId, 10);
  const { canModule } = useAuth();
  const canEditCv = canModule('hr_cv_screening', 'edit');

  const [tab, setTab] = useState('positions');
  const [positions,   setPositions]   = useState([]);
  const [templates,   setTemplates]   = useState([]);
  const [candidates,  setCandidates]  = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [autoScreening, setAutoScreening] = useState(true);
  const [unscreened, setUnscreened] = useState([]);
  const [togglingAuto, setTogglingAuto] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Form Responses tab: view state, held HERE and not in the tab ──────────
  // reload() flips `loading`, and the render below is
  // `{loading ? <spinner/> : <>…tabs…</>}`, so every refresh UNMOUNTS the tab
  // and takes its local state with it. FormResponsesTab's own Refresh button
  // calls reload(), so state living inside it would be destroyed by a control
  // sitting a few pixels above the search box.
  //
  // Same trap InterviewScheduling.jsx:44 documents and solves the same way:
  // lift it OUT of the subtree that gets unmounted, rather than teach the tab
  // to survive. Nothing here changes how tabs render.
  //
  // `selectedSubmission` is included because it had this bug ALREADY — Refresh
  // has always blanked the detail pane. Lifting search but not selection would
  // have turned a long-standing annoyance into an apparent new regression:
  // search, refresh, and you would be left with a filtered list and an empty
  // pane that looked like the filter caused it.
  //
  // The in-flight flags (exporting / sendingSchedule / sendingReject) stay
  // LOCAL on purpose. Those SHOULD die with the component: a lifted one
  // survives the unmount that cancelled the work it was tracking, and leaves a
  // spinner that never stops.
  const [formSearch, setFormSearch] = useState('');
  const [formPage, setFormPage] = useState(1);
  const [formReviewFilter, setFormReviewFilter] = useState('all');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [queueSearch, setQueueSearch] = useState('');
  const [queuePage, setQueuePage] = useState(1);
  const [queueSelected, setQueueSelected] = useState(null);
  const [examBucket, setExamBucket] = useState('waiting');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [posRes, tplRes, candRes, subRes, setRes, unsRes] = await Promise.all([
        cvAPI.listPositions(deptId),
        cvAPI.listEmailTemplates(deptId),
        cvAPI.getCandidates({ departmentId: deptId }),
        formsAPI.getSubmissionsEnriched(deptId),
        cvScreeningAPI.getSettings(deptId),
        cvScreeningAPI.listUnscreened(deptId),
      ]);
      setPositions(posRes.data);
      setTemplates(tplRes.data);
      setCandidates(candRes.data);
      setSubmissions(subRes.data);
      setSelectedSubmission(previous => previous
        ? subRes.data.find(sub => sub.id === previous.id) || previous : null);
      setAutoScreening(setRes.data.auto_screening_enabled);
      setUnscreened(unsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load CV screening data');
    } finally {
      setLoading(false);
    }
  }, [deptId]);

  const toggleAutoScreening = async () => {
    setTogglingAuto(true);
    try {
      const next = !autoScreening;
      await cvScreeningAPI.updateSettings(deptId, { auto_screening_enabled: next });
      setAutoScreening(next);
      toast.success(next ? 'Auto screening turned ON' : 'Auto screening paused');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update auto screening');
    } finally {
      setTogglingAuto(false);
    }
  };

  useEffect(() => { reload(); }, [reload]);

  const applyResponseReview = (submissionId, review) => {
    setSubmissions(previous => previous.map(s => s.id === submissionId ? { ...s, ...review } : s));
  };

  const workflow = useMemo(() => buildFormWorkflow(candidates, submissions), [candidates, submissions]);
  const reviewSubmissions = workflow.latest.filter(s => s.workflow_stage === 'review');
  const submissionIds = new Set(submissions.map(s => s.candidate_id));
  const legacyReview = candidates.filter(c => !submissionIds.has(c.id) && awaitingFormReview(c)
    && !['schedule_sent', 'interview_scheduled', 'interviewed', 'rejected', 'no_response'].includes(c.status));
  const reviewCount = reviewSubmissions.length + legacyReview.length;
  const examCount = workflow.latest.filter(s => ['waiting', 'completed', 'attention'].includes(s.workflow_stage)).length;
  const queueRows = tab === 'review' ? reviewSubmissions : workflow.latest.filter(s => s.workflow_stage === examBucket);
  const currentQueueSelection = queueRows.find(s => s.id === queueSelected?.id) || null;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <UserGroupIcon className="w-6 h-6 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CV Screening</h1>
            <p className="text-sm text-gray-500">
              Fully automated: incoming CVs are matched to a position, scored, and emailed back — zero manual screening.
            </p>
          </div>
        </div>

        {/* Auto Screening toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Auto Screening</span>
          <button
            onClick={toggleAutoScreening}
            disabled={togglingAuto || !canEditCv}
            role="switch"
            aria-checked={autoScreening}
            title={!canEditCv
              ? "🔒 You don't have permission"
              : autoScreening
                ? 'Incoming CVs are automatically screened'
                : 'Incoming CVs are saved to the project folder only'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              autoScreening ? 'bg-green-600' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              autoScreening ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
          <span className={`text-xs font-semibold ${autoScreening ? 'text-green-700' : 'text-gray-500'}`}>
            {autoScreening ? 'ON' : 'OFF'}
          </span>
        </div>
      </div>

      {/* Paused banner */}
      {!autoScreening && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-4 py-3 text-sm flex items-center gap-2">
          <span>⏸️</span>
          <span>
            Auto screening is paused. CVs are being saved to the project folder only.
            {unscreened.length > 0 && (
              <strong className="ml-1">{unscreened.length} unscreened CV{unscreened.length === 1 ? '' : 's'} waiting.</strong>
            )}
          </span>
        </div>
      )}

      {/* Tab strip */}
      <div className="flex gap-1 overflow-x-auto border-b border-gray-200">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex min-h-[44px] shrink-0 items-center gap-2 whitespace-nowrap px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key
                ? 'border-green-600 text-green-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
            {key === 'candidates' && candidates.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {candidates.length}
              </span>
            )}
            {key === 'candidates' && unscreened.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                    title={`${unscreened.length} unscreened CVs waiting`}>
                {unscreened.length} new
              </span>
            )}
            {key === 'review' && reviewCount > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {reviewCount}
              </span>
            )}
            {key === 'exams' && examCount > 0 && <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">{examCount}</span>}
            {key === 'forms' && submissions.length > 0 && (
              <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                {submissions.length}
              </span>
            )}
            {key === 'forms' && submissions.some(s => !s.reviewed_at) && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
                {submissions.filter(s => !s.reviewed_at).length} unreviewed
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
        </div>
      ) : (
        <>
          {tab === 'positions'  && <PositionsTab   deptId={deptId} positions={positions} reload={reload} />}
          {tab === 'templates'  && <TemplatesTab   templates={templates} reload={reload} />}
          {tab === 'candidates' && <CandidatesTab  candidates={candidates} positions={positions} unscreened={unscreened} setUnscreened={setUnscreened} reload={reload} />}
          {(tab === 'review' || tab === 'exams') && <div className="space-y-4">
            {tab === 'exams' && <div className="flex flex-wrap gap-2" aria-label="Exam filters">
              {[['waiting', 'Waiting'], ['completed', 'Completed'], ['attention', 'Needs Attention']].map(([key, label]) => (
                <button key={key} onClick={() => { setExamBucket(key); setQueuePage(1); setQueueSelected(null); }}
                  aria-pressed={examBucket === key}
                  className={`px-4 py-2 rounded-lg text-sm border ${examBucket === key ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'bg-white border-gray-200 text-gray-600'}`}>
                  {label} ({workflow.latest.filter(s => s.workflow_stage === key).length})
                </button>
              ))}
            </div>}
            <p className="text-sm text-gray-500">{tab === 'review'
              ? 'Newest forms first. Review the answers and send an exam link. Successfully emailed candidates move to Exam Tracking.'
              : 'Track invited candidates, review completed exams, and send the final scheduling or rejection email. Refresh to load new submissions.'}</p>
            <FormResponsesTab submissions={queueRows} deptId={deptId} reload={reload}
              onReviewed={applyResponseReview}
              title={tab === 'review' ? 'Form Review' : stageLabels[examBucket]} queueMode
              search={queueSearch} setSearch={setQueueSearch} page={queuePage} setPage={setQueuePage}
              selected={currentQueueSelection} setSelected={setQueueSelected} />
            {tab === 'review' && legacyReview.length > 0 && <FormReviewTab candidates={legacyReview} reload={reload} />}
          </div>}
          {/* Fed by its own endpoint, not by `candidates`. Every exclusion the
              list applies is computed server-side against send_policy and
              recipient_policy; passing the shared array in would invite a
              client-side reimplementation of rules that exist in one place. */}
          {tab === 'followups'  && <FollowupsTab   deptId={deptId} canEdit={canEditCv} />}
          {/* search / page / selected are props, not local state — see the
              note beside their declarations. */}
          {tab === 'forms'      && (
            <FormResponsesTab
              submissions={workflow.history}
              onReviewed={applyResponseReview}
              reviewFilter={formReviewFilter}
              setReviewFilter={setFormReviewFilter}
              deptId={deptId}
              reload={reload}
              search={formSearch}
              setSearch={setFormSearch}
              page={formPage}
              setPage={setFormPage}
              selected={workflow.history.find(s => s.id === selectedSubmission?.id) || selectedSubmission}
              setSelected={setSelectedSubmission}
            />
          )}
        </>
      )}
    </div>
  );
}


/* ─────────────────────────── Positions ─────────────────────────── */

function PositionsTab({ deptId, positions, reload }) {
  const { canModule } = useAuth();
  const canAdd = canModule('hr_cv_screening', 'add');
  const [editing, setEditing]   = useState(null);  // position obj or {} for new
  const [saving, setSaving]     = useState(false);
  const [questions, setQuestions] = useState([]);  // dept form questions for threshold dropdown

  const [banks, setBanks] = useState([]);        // selectable English tests

  const blank = {
    title: '', company_name: '', criteria_text: '', additional_criteria: '',
    form_screening_enabled: false, form_thresholds: [], active: true,
    english_test_bank: null,
  };

  // Load the department's form questions so thresholds can reference them by id.
  useEffect(() => {
    formsAPI.getQuestions(deptId)
      .then((res) => setQuestions(res.data || []))
      .catch(() => setQuestions([]));
  }, [deptId]);

  // The English test dropdown's options. Keys and labels only — the endpoint
  // returns no questions and no answers, so this is safe to hold in the bundle.
  // Failure is silent and leaves the list empty: an unreachable registry must
  // not stop someone editing a position's title.
  useEffect(() => {
    assessmentsAPI.listBanks()
      .then((res) => setBanks(res.data || []))
      .catch(() => setBanks([]));
  }, []);

  const save = async () => {
    if (!editing.title?.trim()) {
      toast.error('Position title is required');
      return;
    }
    const thresholds = (editing.form_thresholds || []).filter(
      (t) => t.question_id && t.type && String(t.value).trim() !== ''
    );
    setSaving(true);
    try {
      if (editing.id) {
        await cvAPI.updatePosition(editing.id, {
          title: editing.title,
          company_name: editing.company_name?.trim() || null,
          criteria_text: editing.criteria_text,
          additional_criteria: editing.additional_criteria || '',
          form_screening_enabled: !!editing.form_screening_enabled,
          form_thresholds: thresholds,
          // Listed explicitly because this payload is written out by hand, and
          // a field omitted here is silently dropped — the same trap that made
          // company_name settable but never saved. '' is normalised to null so
          // "No test" clears the column rather than sending an empty key the
          // backend would reject.
          english_test_bank: editing.english_test_bank || null,
          active: editing.active,
        });
        toast.success('Position updated');
      } else {
        await cvAPI.createPosition({
          ...editing, form_thresholds: thresholds, department_id: deptId,
        });
        toast.success('Position created');
      }
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pos) => {
    if (!window.confirm(`Delete position "${pos.title}"?`)) return;
    try {
      await cvAPI.deletePosition(pos.id);
      toast.success('Position deleted');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const toggleActive = async (pos) => {
    try {
      await cvAPI.updatePosition(pos.id, { active: !pos.active });
      reload();
    } catch {
      toast.error('Could not toggle position');
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 className="font-semibold text-gray-800">Open Positions</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Each incoming CV is matched against the active positions below — the best fit is picked automatically.
          </p>
        </div>
        <button
          onClick={() => setEditing({ ...blank })}
          disabled={!canAdd}
          title={canAdd ? '' : "🔒 You don't have permission"}
          className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusIcon className="w-4 h-4" /> Add Position
        </button>
      </div>

      {positions.length === 0 ? (
        <div className="text-center py-12 text-gray-400">
          <BriefcaseIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No positions yet. Add one so the AI knows what to screen for.</p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {positions.map((p) => (
            <li key={p.id} className="px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50/50">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-medium text-gray-800">{p.title}</h3>
                  {p.active ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">Active</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">Inactive</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1 line-clamp-2 whitespace-pre-wrap">
                  {p.criteria_text || <em className="text-gray-300">no criteria set yet</em>}
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => toggleActive(p)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title={p.active ? 'Deactivate' : 'Activate'}
                >
                  {p.active ? <CheckCircleIcon className="w-4 h-4" /> : <XCircleIcon className="w-4 h-4" />}
                </button>
                <button
                  onClick={() => setEditing({ ...p })}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
                  title="Edit"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => remove(p)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)}
             title={editing?.id ? 'Edit Position' : 'Add Position'}>
        {editing && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position Title *</label>
              <input
                value={editing.title}
                onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                placeholder="e.g. IT Admin, Sales Executive, Manager"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company Name
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  Used for {'{{company_name}}'} in emails — falls back to the global setting when blank
                </span>
              </label>
              <input
                value={editing.company_name || ''}
                onChange={(e) => setEditing({ ...editing, company_name: e.target.value })}
                placeholder="e.g. PATHE Academy, UN Consortium"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Screening Criteria
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  What the ideal candidate looks like
                </span>
              </label>
              <textarea
                value={editing.criteria_text}
                onChange={(e) => setEditing({ ...editing, criteria_text: e.target.value })}
                rows={8}
                placeholder={'- Minimum 3 years of relevant experience\n- Strong communication skills\n- Bachelor\'s degree preferred\n- Familiarity with [specific tools]'}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional Checks (Flag if Missing)
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  Informational only — never rejects or affects the score
                </span>
              </label>
              <textarea
                value={editing.additional_criteria || ''}
                onChange={(e) => setEditing({ ...editing, additional_criteria: e.target.value })}
                rows={5}
                placeholder={'e.g. Location within 1hr of Colombo 5, Expected salary, Notice period'}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300 font-mono"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                On-site English Test
                <span className="ml-2 text-xs text-gray-400 font-normal">
                  Sat on an office laptop, marked by the server — not sent to the candidate
                </span>
              </label>
              <select
                value={editing.english_test_bank || ''}
                onChange={(e) => setEditing({
                  ...editing,
                  // '' is the "No test" option. Normalised to null here as well
                  // as in save(), so the value in state matches what is sent.
                  english_test_bank: e.target.value || null,
                })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
              >
                <option value="">No test</option>
                {banks.map((b) => (
                  <option key={b.key} value={b.key}>
                    {b.label} — {b.total} questions, pass {b.pass_mark}%
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                With no test selected, candidate rows for this position show no English
                Test button at all.
              </p>
            </div>

            {/* ── Form answer screening (hold/pass/review) ── */}
            <div className="border border-gray-200 rounded-xl p-3 bg-gray-50/50 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={!!editing.form_screening_enabled}
                  onChange={(e) => setEditing({ ...editing, form_screening_enabled: e.target.checked })}
                  className="w-4 h-4 rounded text-green-600"
                />
                Enable form answer screening
              </label>
              <p className="text-xs text-gray-400 -mt-1">
                When a candidate submits the form, answers are checked against these thresholds.
                Any failed or unclear answer holds the candidate for HR review before the interview
                scheduling link is sent.
              </p>

              {editing.form_screening_enabled && (
                <ThresholdBuilder
                  questions={questions}
                  positionId={editing.id}
                  thresholds={editing.form_thresholds || []}
                  onChange={(next) => setEditing({ ...editing, form_thresholds: next })}
                />
              )}
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={editing.active}
                onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
                className="w-4 h-4 rounded text-green-600"
              />
              Active — include in CV matching
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)}
                      className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                      className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60">
                {saving ? 'Saving...' : (editing.id ? 'Save changes' : 'Create position')}
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}


/* ──────────────────────── Threshold Builder ─────────────────────── */

const THRESHOLD_TYPES = [
  { value: 'max_number',       label: 'Max number' },
  { value: 'min_number',       label: 'Min number' },
  { value: 'must_contain',     label: 'Must contain' },
  { value: 'must_not_contain', label: 'Must not contain' },
];

function ThresholdBuilder({ questions, positionId, thresholds, onChange }) {
  // Only questions this position actually asks: common (position_id null) +
  // its own. A new/unsaved position (no id yet) can reference common only.
  const activeQuestions = (questions || []).filter(
    (q) => q.active && (q.position_id == null || q.position_id === positionId),
  );

  const addRow = () => {
    const first = activeQuestions[0];
    onChange([
      ...thresholds,
      {
        question_id: first?.id || '',
        label: first?.question_text || '',
        type: 'max_number',
        value: '',
      },
    ]);
  };

  const updateRow = (idx, patch) => {
    onChange(thresholds.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
  };

  const removeRow = (idx) => onChange(thresholds.filter((_, i) => i !== idx));

  if (activeQuestions.length === 0) {
    return (
      <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
        No active form questions apply to this position yet (common questions, plus any scoped to
        this position). Add questions in the Form Builder first, then return here to set thresholds.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {thresholds.length === 0 && (
        <p className="text-xs text-gray-400">No thresholds yet — add one below.</p>
      )}

      {thresholds.map((t, idx) => {
        const isText = t.type === 'must_contain' || t.type === 'must_not_contain';
        return (
          <div key={idx} className="flex flex-wrap items-center gap-2 bg-white border border-gray-200 rounded-lg p-2">
            {/* Question */}
            <select
              value={t.question_id}
              onChange={(e) => {
                const q = activeQuestions.find((x) => String(x.id) === e.target.value);
                updateRow(idx, { question_id: q ? q.id : '', label: q ? q.question_text : '' });
              }}
              className="flex-1 min-w-[10rem] px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            >
              {activeQuestions.map((q) => (
                <option key={q.id} value={q.id}>{q.question_text}</option>
              ))}
            </select>

            {/* Type */}
            <select
              value={t.type}
              onChange={(e) => updateRow(idx, { type: e.target.value })}
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            >
              {THRESHOLD_TYPES.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Value */}
            <input
              type={isText ? 'text' : 'number'}
              value={t.value}
              onChange={(e) => updateRow(idx, { value: e.target.value })}
              placeholder={isText ? 'text…' : 'e.g. 70000'}
              className="w-28 px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
            />

            <button
              type="button"
              onClick={() => removeRow(idx)}
              className="p-1.5 text-gray-400 hover:text-red-500 rounded"
              title="Remove threshold"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        );
      })}

      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700 font-medium"
      >
        <PlusIcon className="w-4 h-4" /> Add threshold
      </button>
    </div>
  );
}


/* ─────────────────────────── Templates ─────────────────────────── */

function TemplatesTab({ templates, reload }) {
  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
        <p className="font-medium mb-1">How auto-emails work</p>
        <p className="text-blue-800 text-xs leading-relaxed">
          When a CV is scored, the system picks one of the first three templates based on the score and — if the template is <strong>ON</strong> — sends it automatically.
          The <strong>Form Invitation</strong> goes to interview candidates with a unique <code className="bg-blue-100 px-1 rounded">{'{{form_link}}'}</code>.
          The <strong>Interview Scheduling</strong> email goes out after the candidate submits their form, with a unique <code className="bg-blue-100 px-1 rounded">{'{{schedule_link}}'}</code>.
          Common placeholders: <code className="bg-blue-100 px-1 rounded">{'{{candidate_name}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{position_title}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{company_name}}'}</code>,{' '}
          <code className="bg-blue-100 px-1 rounded">{'{{score}}'}</code>.
        </p>
      </div>

      {['interview', 'details', 'rejection', 'form_invite', 'schedule_invite'].map((type) => {
        const tpl = templates.find((t) => t.template_type === type);
        if (!tpl) return null;
        return <TemplateCard key={tpl.id} template={tpl} reload={reload} />;
      })}
    </div>
  );
}

function TemplateCard({ template, reload }) {
  const meta = TIER_META[template.template_type];
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody]       = useState(template.body);
  const [active, setActive]   = useState(template.active);
  const [saving, setSaving]   = useState(false);
  const dirty = subject !== template.subject || body !== template.body || active !== template.active;

  const save = async () => {
    setSaving(true);
    try {
      await cvAPI.updateEmailTemplate(template.id, { subject, body, active });
      toast.success('Template saved');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (next) => {
    setActive(next);
    try {
      await cvAPI.updateEmailTemplate(template.id, { active: next });
      toast.success(next ? 'Template enabled' : 'Template disabled');
      reload();
    } catch {
      setActive(!next);  // rollback on failure
      toast.error('Could not toggle template');
    }
  };

  return (
    <div className={`bg-white rounded-2xl border-2 ${meta.color.replace(/bg-\S+|text-\S+/g, '').trim()} overflow-hidden`}>
      <div className={`px-5 py-3 ${meta.color} border-b flex items-center justify-between`}>
        <div>
          <h3 className="font-semibold">{meta.label}</h3>
          <p className="text-xs opacity-80">{meta.range}</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
          <span>{active ? 'ON' : 'OFF'}</span>
          <span className="relative inline-block w-10 h-5">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => quickToggle(e.target.checked)}
              className="opacity-0 absolute w-0 h-0"
            />
            <span className={`absolute inset-0 rounded-full transition-colors ${active ? 'bg-green-600' : 'bg-gray-300'}`}/>
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${active ? 'translate-x-5' : ''}`}/>
          </span>
        </label>
      </div>
      <div className="p-5 space-y-3">
        {template.template_type === 'form_invite' && (
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-xs text-purple-800">
            <strong>{'{{form_link}}'}</strong> is required — it's automatically replaced with each candidate's unique form URL.
            Don't remove it from the body.
          </div>
        )}
        {template.template_type === 'schedule_invite' && (
          <div className="bg-cyan-50 border border-cyan-200 rounded-lg px-3 py-2 text-xs text-cyan-800">
            <strong>{'{{schedule_link}}'}</strong> is required — it's automatically replaced with each candidate's unique interview scheduling URL.
            Don't remove it from the body.
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Subject</label>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Body</label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={8}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-300 font-mono"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────── Candidates ─────────────────────────── */

/**
 * The candidate's email, with an inline correction.
 *
 * This exists because the send guard refuses a company/forwarding address, and
 * until now the ONLY way to change candidate.email was as a side effect of
 * sending from the review modal — candidates in this table had no route at all.
 * Blocking a send with no way to fix the address would be a dead end.
 *
 * `email_blocked_reason` is computed server-side by the same resolver the send
 * guard uses, so the badge here can never disagree with what actually happens
 * on send.
 */
/* Linked-set panel.
 *
 * Opens the set of candidate rows asserted to be the same PERSON. It never
 * merges: the three actions are Confirm (promote a suggestion), Unlink
 * (withdraw an assertion, a state change and not a delete) and "Not the same
 * person" (a negative link, which is what stops the pair being suggested again
 * on every future arrival).
 *
 * CONFLICTS RENDER ABOVE THE ACTIONS, not beside them. Two live links, or one
 * row rejected while another is interview_scheduled, are exactly the cases
 * where acting without looking causes the damage, so they are on screen before
 * anything is clickable.
 *
 * It does NOT call reload(). reload() flips the parent's loading flag, which
 * unmounts this panel mid-action - the same trap documented at runScreen above.
 * The caller re-syncs the list when the panel closes.
 */
function LinkedSetPanel({ candidate, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const load = React.useCallback(async () => {
    setErr(null);
    try {
      const res = await cvAPI.getCandidateLinks(candidate.id);
      setData(res.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load the linked set');
    }
  }, [candidate.id]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, linkId, label) => {
    setBusy(true);
    try {
      await fn(linkId);
      toast.success(label);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (label + ' failed'));
    } finally {
      setBusy(false);
    }
  };

  if (err) return <div className="text-sm text-red-600">{err}</div>;
  if (!data) return <div className="text-sm text-gray-500">Loading...</div>;

  const suggested = (data.edges || []).filter((e) => e.kind === 'suggested' && e.active);
  const confirmed = (data.edges || []).filter((e) => e.kind === 'confirmed' && e.active);

  return (
    <div className="space-y-4">
      {data.conflicts && data.conflicts.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-3">
          <div className="text-xs font-semibold text-amber-800 mb-1">Read this before acting</div>
          <ul className="list-disc pl-5 space-y-1">
            {data.conflicts.map((c, i) => (
              <li key={i} className="text-xs text-amber-900">{c}</li>
            ))}
          </ul>
        </div>
      )}

      {data.spans_positions && (
        <div className="text-xs text-gray-600">
          These rows sit on different positions, so this reads as a second
          application rather than a duplicate. The link is the same either way.
        </div>
      )}

      <div>
        <div className="text-xs font-semibold text-gray-500 mb-1">
          {data.members.length} rows asserted to be one person
        </div>
        <table className="w-full text-xs">
          <tbody>
            {data.members.map((m) => (
              <tr key={m.candidate_id} className="border-t border-gray-100">
                <td className="py-1 pr-2 font-medium text-gray-800">#{m.candidate_id} {m.name}</td>
                <td className="py-1 pr-2 text-gray-600">{m.status}</td>
                <td className="py-1 pr-2 text-gray-500">{m.position_title || '-'}</td>
                <td className="py-1 text-gray-500">
                  {m.has_live_form_link && <span title="holds a live form link">form</span>}
                  {m.has_live_form_link && m.has_live_schedule_link && ' / '}
                  {m.has_live_schedule_link && <span title="holds a live scheduling link">schedule</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {suggested.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-1">Awaiting review</div>
          {suggested.map((e) => (
            <div key={e.link_id} className="flex items-center gap-2 py-1">
              <span className="text-xs text-gray-600">
                #{e.candidate_a_id} / #{e.candidate_b_id} matched on {e.signals.join(', ')}
              </span>
              <button disabled={busy}
                onClick={() => act(cvAPI.confirmLink, e.link_id, 'Linked')}
                className="text-xs px-2 py-0.5 rounded bg-green-600 text-white disabled:opacity-50">
                Same person
              </button>
              <button disabled={busy}
                onClick={() => act((id) => cvAPI.rejectLink(id, 'reviewed'), e.link_id, 'Recorded as different people')}
                className="text-xs px-2 py-0.5 rounded border border-gray-300 disabled:opacity-50">
                Not the same person
              </button>
            </div>
          ))}
        </div>
      )}

      {confirmed.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 mb-1">Linked</div>
          {confirmed.map((e) => (
            <div key={e.link_id} className="flex items-center gap-2 py-1">
              <span className="text-xs text-gray-600">
                #{e.candidate_a_id} / #{e.candidate_b_id} - {e.signals.join(', ')}
                {e.source === 'auto_intake' && ' (automatic)'}
              </span>
              <button disabled={busy}
                onClick={() => act((id) => cvAPI.unlinkLink(id, 'unlinked from panel'), e.link_id, 'Unlinked')}
                className="text-xs px-2 py-0.5 rounded border border-gray-300 disabled:opacity-50">
                Unlink
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="text-[11px] text-gray-400">
        Linking never merges these rows. Nothing here deletes a candidate, a form
        submission, a booking or a follow-up.
      </div>
    </div>
  );
}


function CandidateEmailCell({ candidate: c, canEdit, reload }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(c.email || '');
  const [saving, setSaving] = useState(false);

  const blocked = !!c.email_blocked_reason;

  const save = async () => {
    const next = value.trim();
    if (!next || next === c.email) { setEditing(false); return; }
    setSaving(true);
    try {
      await cvAPI.updateCandidateEmail(c.id, next);
      toast.success('Email updated');
      setEditing(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not update the email');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1 mt-0.5">
        <input
          type="email"
          value={value}
          autoFocus
          disabled={saving}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          className="text-xs px-2 py-1 border border-gray-300 rounded-md w-56 focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50"
        />
        <button onClick={save} disabled={saving}
                className="text-xs px-2 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={() => { setValue(c.email || ''); setEditing(false); }}
                className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    );
  }

  return (
    <div className="mt-0.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`text-xs ${blocked ? 'text-orange-700 font-medium' : 'text-gray-500'}`}>
          {c.email || '—'}
        </span>
        {canEdit && (
          <button
            onClick={() => { setValue(c.email || ''); setEditing(true); }}
            title="Correct this address"
            className="text-xs text-primary-600 hover:text-primary-700 underline decoration-dotted"
          >
            edit
          </button>
        )}
      </div>
      {blocked && (
        <div className="mt-0.5 inline-flex items-start gap-1 text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5 max-w-md">
          <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>Needs a real email — {c.email_blocked_reason}</span>
        </div>
      )}
    </div>
  );
}

/**
 * The English assessment result, as a chip.
 *
 * DISPLAY ONLY, following the score_hold_reason precedent exactly: it gates
 * nothing, hides no button, and no send decision reads it. A candidate who has
 * not sat the test renders nothing at all rather than an empty placeholder —
 * most rows will never have one, and a column of dashes reads as a defect.
 *
 * `english_assessment` is null both for "never assessed" and for "a link was
 * minted but never returned". Those are the same thing to a reader of this
 * table: there is no result. The distinction lives in the database if anyone
 * ever needs it.
 */
function EnglishAssessmentChip({ candidate: c }) {
  const a = c.english_assessment;
  if (!a) return null;

  const passed = a.passed;
  const tone = passed
    ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
    : 'text-rose-700 bg-rose-50 border-rose-200';
  const when = a.submitted_at ? new Date(a.submitted_at).toLocaleDateString() : null;

  return (
    <span
      title={`English assessment: ${a.pct}%${when ? ` on ${when}` : ''}`}
      className={`inline-flex items-center gap-1 text-xs font-medium border px-2 py-0.5 rounded-full ${tone}`}
    >
      <AcademicCapIcon className="w-3.5 h-3.5 shrink-0" />
      English {a.pct}%
    </span>
  );
}

/**
 * The minted assessment link, shown once so HR can carry it to the candidate.
 *
 * WHY THE PRIVATE-WINDOW INSTRUCTION IS THE LOUDEST THING HERE. The link is
 * safe to open; the BROWSER is not. A normal window on this laptop still holds
 * the HR session — AuthContext keeps the bearer token in localStorage
 * (context/AuthContext.jsx) and it survives until logout — so a candidate left
 * alone with that tab can type "/" in the address bar and reach the whole
 * authenticated app: every other candidate's CV, their scores, and the admin
 * panel if the person who minted the link is a super admin.
 *
 * The assessment page itself is already fenced: it is a standalone static file
 * at public/assessment.html, served straight off disk by nginx, so it never
 * loads the React bundle and no authenticated code path exists inside it. A
 * private window closes the remaining hole, which is the SESSION, not the page.
 *
 * The link is shown, never auto-opened. Opening it here would start the
 * candidate's clock in an HR-session window — precisely the wrong thing.
 */
function AssessmentLinkModal({ candidate, data, onClose }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard is blocked on insecure origins and in some locked-down
      // browsers. The input below is selectable, so there is always a manual
      // route — this failure must not look like the link itself failed.
      toast.error('Could not copy automatically — select the link and copy it');
    }
  };

  const expires = data.expires_at ? new Date(data.expires_at) : null;

  return (
    <Modal open onClose={onClose} title={`English test — ${candidate.name}`} size="lg">
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5">
          <ShieldExclamationIcon className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">
              Open this link in a PRIVATE / INCOGNITO window before handing over the laptop.
            </p>
            <p className="mt-1 text-xs text-amber-800">
              A normal window still holds your login. From it the candidate could reach the
              rest of the app, including other candidates&apos; CVs.
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Assessment link</label>
          <div className="flex items-center gap-2">
            <input
              readOnly
              value={data.url}
              onFocus={(e) => e.target.select()}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-xs font-mono bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            <button
              onClick={copy}
              className="flex items-center gap-1 text-sm px-3 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 shrink-0"
            >
              <ClipboardIcon className="w-4 h-4" />
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </div>

        <div className="text-xs text-gray-500 space-y-1">
          {expires && (
            <p>
              Expires <span className="font-medium text-gray-700">{expires.toLocaleString()}</span>
              {' '}— after that the link stops working and a new one must be issued.
            </p>
          )}
          <p>
            One link at a time. Issuing another for this candidate immediately kills this one.
          </p>
          <p>
            The result appears on this row once they submit. The candidate is never shown
            their score.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Inline position correction, deliberately built like CandidateEmailCell — same
 * PATCH endpoint, same access check, same edit/save/cancel shape.
 *
 * It exists because a wrong match had no safe remedy: rescreening re-ran the
 * pipeline and emailed the candidate (KNOWN-ISSUES #5), so the only route to a
 * correct position was to contact the applicant again. And the position is not
 * decorative: it decides which form questions they see, which company name
 * their emails carry, and which interview slots they are offered.
 *
 * This is now also the click-through for the position SUGGESTION that Refresh
 * returns. Refresh recomputes the match and never writes it — nothing on the
 * row records whether the stored position came from the model or from a human,
 * so overwriting could silently undo a hand correction. Writing it stays a
 * deliberate act, and this is where that act happens.
 *
 * The title is never edited directly — the server derives it from the chosen
 * position. A title without a backing id is exactly the state that made a
 * failed AI match look like a successful one, and this must not create it.
 */
// The screening path stores position_id and matched_position_title together or
// stores neither, so an unmatched candidate has no title at all. Render that
// absence as an explicit state rather than a dash: "no position" changes which
// form questions, company name and interview slots the candidate gets, and a
// dash reads like missing display data instead of a decision that has to be
// made. NO_MATCH is a UI label only — it is never written to the database,
// which is the whole reason the column is NULL.
const NO_MATCH = 'No Match';

// Same idea as NO_MATCH, one column over: a screening that FAILED stores a NULL
// score, and NULL has to be reachable from the filters or it is invisible in a
// list of 300. Also a UI label only, never written to the database.
const NOT_SCORED = 'Not scored';

// Derived from the NULL score, deliberately NOT from a status — there is no
// screening_failed enum value and adding one to a Postgres enum is a one-way
// door.
//
// The screening_notes half is what stops a row that simply has not been
// screened YET from being swept in: the placeholder candidate created at intake
// also carries a NULL score, but carries no notes until the pipeline writes
// them. Every failure path writes one ("Could not extract text…", "AI returned
// unparseable response", "Screening response was truncated…"), so notes present
// + score absent means screening ran and produced nothing.
const isNotScored = (c) => c.screening_score == null && !!c.screening_notes;

// Has this row's screening moved since the candidate was last told anything?
//
// THE NULL CASE IS THE WHOLE POINT. A candidate who has never been emailed has
// email_sent_at === null, and `new Date(null)` is the epoch — so a naive
// `refreshed > sent` would read every never-emailed row as stale and silently
// drop the entire unscreened intake out of bulk sending. Those rows must STAY
// sendable; there is nothing they have been told that the new score could
// contradict. Both nulls are answered explicitly, before any date arithmetic.
//
// Used by every filter that decides what a BULK action sends. Deliberately not
// consulted by per-candidate actions: one person reading one row and clicking
// send is the informed act this guard exists to preserve.
const refreshedSinceLastEmail = (c) => {
  if (!c?.screening_refreshed_at) return false;   // never refreshed
  if (!c?.email_sent_at) return false;            // never emailed — stays sendable
  return new Date(c.screening_refreshed_at) > new Date(c.email_sent_at);
};

// Is this candidate sitting in the Form Review tab, awaiting a human read of
// their form answers?
//
// ONE predicate with two consumers, which is the point: FormReviewTab uses it
// to build its list, and the Candidates row uses it to HIDE the tier-send
// button. Without a shared definition the two would drift and a candidate would
// end up with a tier-send button in one tab and a Reject button in the other,
// both sending the same email. Kept module-level so neither can fork it.
const awaitingFormReview = (c) =>
  (c.has_form_submission && !c.form_screening_override && c.status === 'form_submitted')
  || (c.form_screening_status === 'pending_hr_review' && !c.form_screening_override);

// A screening reason is an object since the verdict became derived from it:
// { criterion, finding, disqualifying, off_criteria }. Rows screened before
// that hold bare strings. Both shapes render through here — rendering the
// object directly gives "[object Object]", and filtering to strings would make
// every new reason vanish, which is the failure this whole change is about.
//
// A criterion that was checked and MET is shown, marked, not hidden: it is the
// evidence that the candidate cleared it, and burying it is how six people came
// to sit rejected on reasons that cleared them.
function reasonLines(reasons) {
  return (reasons || []).map((r) => {
    if (typeof r === 'string') return { text: r, disqualifying: true, off: false };
    if (!r || typeof r !== 'object') return null;
    const crit = (r.criterion || '').trim();
    const find = (r.finding || '').trim();
    const text = crit && find ? `${crit}: ${find}` : (crit || find);
    if (!text) return null;
    return { text, disqualifying: !!r.disqualifying, off: !!r.off_criteria };
  }).filter(Boolean);
}

/**
 * What the last refresh did to this row.
 *
 * Three things, in the order they matter:
 *   1. A REFUSAL, when refresh declined because the new screening failed. The
 *      score is unchanged and the user must see why, or the button reads as
 *      broken and they click it again.
 *   2. The score old -> new, and the verdict flip if there was one. A row can
 *      become newly rejection-tier here with no email going anywhere, and that
 *      is exactly the change a person needs to notice.
 *   3. The recomputed position as a SUGGESTION when it disagrees with what is
 *      stored. Never written — nothing on the row records whether the stored
 *      position came from the model or from a human correction, so overwriting
 *      would silently undo hand-corrected rows. The click-through is the same
 *      inline editor in CandidatePositionCell, which writes it deliberately.
 */
function RefreshOutcome({ result: d, positions, candidate: c, reload, canEdit, onDismiss }) {
  const [applying, setApplying] = useState(false);

  const applySuggestion = async () => {
    setApplying(true);
    try {
      await cvAPI.updateCandidate(c.id, { position_id: d.suggested_position_id });
      toast.success('Position updated');
      onDismiss();
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update position');
    } finally {
      setApplying(false);
    }
  };

  if (!d.refreshed) {
    return (
      <div className="mt-1.5 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 max-w-md">
        <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
        <span className="flex-1">
          <span className="font-medium">Not refreshed.</span> {d.reason}
        </span>
        <button onClick={onDismiss} className="text-amber-500 hover:text-amber-700 shrink-0">✕</button>
      </div>
    );
  }

  const oldScore = d.changes?.screening_score?.old;
  const newScore = d.changes?.screening_score?.new;
  const flipped  = d.tier?.old !== d.tier?.new;
  const suggests = d.position_differs && d.suggested_position_id != null;

  return (
    <div className="mt-1.5 flex flex-col gap-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-2 py-1.5 max-w-md">
      <div className="flex items-start gap-1.5">
        <ArrowPathIcon className="w-3.5 h-3.5 shrink-0 mt-px text-gray-400" />
        <span className="flex-1 text-gray-700">
          Score <span className="font-medium">{oldScore ?? '—'}%</span>
          {' → '}
          <span className="font-medium">{newScore ?? '—'}%</span>
          {flipped && (
            <>
              {' · verdict '}
              <span className="font-medium">{d.tier?.old ?? 'none'}</span>
              {' → '}
              <span className="font-semibold text-amber-700">{d.tier?.new ?? 'none'}</span>
            </>
          )}
          {/* Said out loud. The whole point of refresh is that the number moved
              and nobody was contacted about it. */}
          <span className="text-gray-400"> · no email sent</span>
        </span>
        <button onClick={onDismiss} className="text-gray-400 hover:text-gray-600 shrink-0">✕</button>
      </div>
      {d.score_hold_reason?.new && !d.score_hold_reason?.old && (
        <div className="text-amber-700 pl-5">Now on hold: {d.score_hold_reason.new}</div>
      )}
      {suggests && (
        <div className="flex items-center gap-2 pl-5 flex-wrap">
          <span className="text-gray-600">
            Screening now suggests{' '}
            <span className="font-medium">{d.suggested_position_title}</span>
            {' '}(row says {d.current_position_title || 'No Match'})
          </span>
          <button
            onClick={applySuggestion}
            disabled={applying || !canEdit}
            title={canEdit ? 'Set this position on the candidate' : "🔒 You don't have permission"}
            className="px-2 py-0.5 border border-blue-200 text-blue-600 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {applying ? '…' : 'Apply'}
          </button>
        </div>
      )}
    </div>
  );
}

function CandidatePositionCell({ candidate: c, positions, canEdit, reload, onFilter }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(c.position_id ?? '');
  const [saving, setSaving] = useState(false);

  // Why this candidate cannot be sent to, straight from the server. Computed
  // by the same resolver the send guard uses, so the badge and the actual
  // refusal can never disagree — and it clears itself when the position is set.
  //
  // This replaces a warning that read "Not linked to a position", which flagged
  // a title with no id. That state no longer exists: the screening path stores
  // both columns or neither. The live problem is not that the row looks wrong,
  // it is that the row cannot be sent to.
  const blocked = c.position_block_reason;

  // Every position is offerable, including deactivated ones. This cell exists
  // to correct a historical record, and a record has to be able to name what
  // actually happened — whether the role is still being hired for is a
  // separate question. The alternative, reactivating a position to do data
  // maintenance, puts it back in front of the AI during screening and back
  // into the filters: turning off a safety to do a repair.
  //
  // Active ones are grouped first so the normal case stays the easy one and
  // reaching for a closed role is deliberate.
  const activePositions   = positions.filter((p) => p.active);
  const inactivePositions = positions.filter((p) => !p.active);

  const save = async () => {
    const next = value === '' ? null : Number(value);
    if (next === (c.position_id ?? null)) { setEditing(false); return; }
    setSaving(true);
    try {
      await cvAPI.updateCandidate(c.id, { position_id: next });
      toast.success(next === null ? 'Position cleared' : 'Position updated');
      setEditing(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail
                  || 'Could not update the position');
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        <select
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={saving}
          className="text-xs px-2 py-1 border border-gray-200 rounded-md max-w-[15rem] focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">— No position —</option>
          {activePositions.length > 0 && (
            <optgroup label="Active">
              {activePositions.map((p) => (
                <option key={p.id} value={p.id}>{p.title}</option>
              ))}
            </optgroup>
          )}
          {inactivePositions.length > 0 && (
            <optgroup label="Inactive">
              {/* The suffix repeats the group label on purpose: a closed
                  <select> shows the selected option's own text and not the
                  group it came from, so without it the row would read as an
                  ordinary match once saved. */}
              {inactivePositions.map((p) => (
                <option key={p.id} value={p.id}>{p.title} (inactive)</option>
              ))}
            </optgroup>
          )}
        </select>
        <button onClick={save} disabled={saving}
                className="text-xs px-2 py-1 rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
          {saving ? '…' : 'Save'}
        </button>
        <button onClick={() => { setValue(c.position_id ?? ''); setEditing(false); }}
                className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {c.matched_position_title ? (
          <button
            onClick={() => onFilter?.(c.matched_position_title)}
            className="text-left text-gray-700 hover:text-green-700 hover:underline"
            title="Filter by this position"
          >
            {c.matched_position_title}
          </button>
        ) : (
          <button
            onClick={() => onFilter?.(NO_MATCH)}
            className="text-left text-gray-500 italic hover:text-green-700 hover:underline"
            title="Show every candidate with no matched position"
          >
            {NO_MATCH}
          </button>
        )}
        {canEdit && (
          <button
            onClick={() => { setValue(c.position_id ?? ''); setEditing(true); }}
            title="Correct the matched position"
            className="text-xs text-primary-600 hover:text-primary-700 underline decoration-dotted"
          >
            edit
          </button>
        )}
      </div>
      {blocked && (
        <div className="mt-0.5 inline-flex items-start gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 max-w-sm">
          <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>Cannot be emailed — {blocked}</span>
        </div>
      )}
    </div>
  );
}

const EXAM_STATUS_LABELS = {
  not_sent: 'Not Sent', sent: 'Sent', started: 'Started', passed: 'Passed',
  failed: 'Failed', expired: 'Expired', send_failed: 'Send Failed', not_configured: 'Not configured',
};
const examStatus = candidate => candidate.exam_status || 'not_configured';
const EXAM_STATUS_COLORS = {
  passed: 'bg-green-100 text-green-800', failed: 'bg-red-100 text-red-800',
  sent: 'bg-blue-100 text-blue-800', started: 'bg-purple-100 text-purple-800',
  expired: 'bg-amber-100 text-amber-800', send_failed: 'bg-red-100 text-red-800',
};

function CandidatesTab({ candidates, positions = [], unscreened = [], setUnscreened, reload }) {
  const { canModule } = useAuth();
  const canAdd = canModule('hr_cv_screening', 'add');
  const canEdit = canModule('hr_cv_screening', 'edit');
  const canSchedule = canModule('hr_interview_scheduling', 'edit');
  const [posFilter,    setPosFilter]    = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [examFilter, setExamFilter] = useState('all');
  const [search,       setSearch]       = useState('');
  const [currentPage,  setCurrentPage]  = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [refreshing,   setRefreshing]   = useState({});
  // Last refresh outcome per candidate id, so the row can show old -> new
  // without a second request. Cleared on reload — the row itself then carries
  // the new value and the badge has nothing left to add.
  const [refreshResult, setRefreshResult] = useState({});
  const [loadingPdf,   setLoadingPdf]   = useState({});
  const [sendingForm,     setSendingForm]     = useState({});
  const [sendingSchedule, setSendingSchedule] = useState({});
  const [sendingTier,     setSendingTier]     = useState({});
  const [mintingTest,     setMintingTest]     = useState({});
  //: {candidate, data} while the minted link is on screen, else null.
  const [testLink,        setTestLink]        = useState(null);
  const [pdfModal,     setPdfModal]     = useState({ open: false, url: null, filename: null });
  const [detailCandidate, setDetailCandidate] = useState(null);   // row → full-detail modal
  // Held on the PARENT, not inside the row. reload() unmounts children, so a
  // panel owned by a row would vanish mid-action - the trap documented at
  // runScreen. The list is re-synced when the panel closes, not while it is open.
  const [linkPanelCandidate, setLinkPanelCandidate] = useState(null);
  // HR Review Before Send state
  const [previewResults,  setPreviewResults]  = useState([]);
  const [showPreview,     setShowPreview]     = useState(false);
  const [sendingPreview,  setSendingPreview]  = useState({});  // {[candidateId]: true}
  const [sendingAll,      setSendingAll]      = useState(false);
  const [selectedDocs, setSelectedDocs] = useState(() => new Set());
  const [screeningNow, setScreeningNow] = useState(false);

  // ── Derived filter options (only values present in current data) ──────
  const uniquePositions = useMemo(() => {
    const seen = new Set();
    const titles = candidates
      .map((c) => c.matched_position_title)
      .filter((t) => t && !seen.has(t) && seen.add(t));
    // Unmatched rows carry no title, so they would otherwise be unreachable
    // from this filter — and they are precisely the rows someone opens this
    // page to find and correct. Offer them as one bucket, listed first.
    //
    // Not-scored is listed ahead of No Match on the same rule the review modal
    // partitions by: the problem that has to be fixed first owns the row. A row
    // with no score also has no position, so it would otherwise hide inside No
    // Match — where the remedy on offer (set the position) is the wrong one. It
    // needs re-screening, not a position.
    const specials = [];
    if (candidates.some(isNotScored)) specials.push(NOT_SCORED);
    if (candidates.some((c) => !c.matched_position_title)) specials.push(NO_MATCH);
    return [...specials, ...titles];
  }, [candidates]);

  const uniqueStatuses = useMemo(() => {
    const seen = new Set();
    return candidates
      .map((c) => c.status)
      .filter((s) => s && !seen.has(s) && seen.add(s));
  }, [candidates]);

  // ── Apply position + status filters AND the name/email search ─────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return candidates.filter((c) => {
      if (posFilter === NOT_SCORED) {
        if (!isNotScored(c)) return false;
      } else if (posFilter === NO_MATCH) {
        // A not-scored row has no position either, but it belongs to its own
        // bucket above rather than to both — same reason the modal partitions
        // before it styles.
        if (c.matched_position_title || isNotScored(c)) return false;
      } else if (posFilter !== 'all' && c.matched_position_title !== posFilter) {
        return false;
      }
      if (examFilter !== 'all' && examStatus(c) !== examFilter) return false;
      if (statusFilter !== 'all' && c.status                !== statusFilter)  return false;
      if (q && !(c.name || '').toLowerCase().includes(q) && !(c.email || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [candidates, posFilter, statusFilter, examFilter, search]);

  // ── Pagination (10 per page) ──────────────────────────────────────────
  // Reset to the first page whenever the filters change.
  useEffect(() => { setCurrentPage(1); }, [posFilter, statusFilter, examFilter, search]);

  // Close the candidate-detail modal on Escape (Modal already closes on
  // background-click and its X button; this adds keyboard dismissal, scoped
  // to this tab without touching the shared Modal component).
  useEffect(() => {
    if (!detailCandidate) return;
    const onKey = (e) => { if (e.key === 'Escape') setDetailCandidate(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detailCandidate]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  // Clamp in case the list shrank (e.g. after a reload) below the current page.
  const page = Math.min(currentPage, totalPages);
  const pageStart = (page - 1) * ITEMS_PER_PAGE;
  const paginatedCandidates = filtered.slice(pageStart, pageStart + ITEMS_PER_PAGE);
  const rangeStart = filtered.length === 0 ? 0 : pageStart + 1;
  const rangeEnd = Math.min(pageStart + ITEMS_PER_PAGE, filtered.length);

  // ── PDF preview ───────────────────────────────────────────────────────
  const openCv = async (candidateId, filename) => {
    setLoadingPdf((p) => ({ ...p, [candidateId]: true }));
    try {
      const res    = await cvAPI.getCv(candidateId);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPdfModal({ open: true, url: blobUrl, filename: filename || 'CV Preview' });
    } catch (err) {
      // The request uses responseType:'blob', so a backend JSON error arrives as
      // a Blob — read it to surface the real reason (e.g. "CV file not found on
      // disk") instead of a generic message.
      let msg = 'Could not load CV';
      try {
        const text = await err.response?.data?.text?.();
        if (text) msg = JSON.parse(text).detail || msg;
      } catch { /* fall back to the generic message */ }
      toast.error(msg);
    } finally {
      setLoadingPdf((p) => ({ ...p, [candidateId]: false }));
    }
  };

  const closePdf = () => {
    if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
    setPdfModal({ open: false, url: null, filename: null });
  };

  // Same CV, new tab. The review modal stays open behind it, which is the whole
  // point: the address is auto-filled from the document, and checking one
  // against the other needs both on screen at once. The in-app preview above
  // would cover the list it is meant to be checked against.
  //
  // Fetches through cvAPI like openCv rather than pointing a tab at the
  // endpoint URL — the route is authenticated and a bare window.open sends no
  // bearer token.
  const openCvInTab = async (candidateId) => {
    // Opened BEFORE the await, deliberately. Popup blockers only allow
    // window.open inside a user gesture, and awaiting first leaves that gesture.
    const tab = window.open('', '_blank');
    setLoadingPdf((p) => ({ ...p, [candidateId]: true }));
    try {
      const res     = await cvAPI.getCv(candidateId);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (tab) {
        tab.location = blobUrl;
        // Not revoked straight away — the tab is still reading from it. A grace
        // period releases it without leaking until the page unloads.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        // Popup blocked. Fall back to the in-app preview rather than doing
        // nothing and looking broken.
        setPdfModal({ open: true, url: blobUrl, filename: 'CV Preview' });
      }
    } catch (err) {
      tab?.close();
      let msg = 'Could not load CV';
      try {
        const text = await err.response?.data?.text?.();
        if (text) msg = JSON.parse(text).detail || msg;
      } catch { /* fall back to the generic message */ }
      toast.error(msg);
    } finally {
      setLoadingPdf((p) => ({ ...p, [candidateId]: false }));
    }
  };

  // ── Refresh ───────────────────────────────────────────────────────────
  // Replaces the sending Rescreen. Recomputes the score against the current
  // position criteria and writes ONLY the screening columns — no email, no
  // status change, no form token. The tier email, if one is now wanted, is a
  // separate deliberate click on the tier-send button beside it.
  const refresh = async (id) => {
    setRefreshing((p) => ({ ...p, [id]: true }));
    try {
      const res = await cvAPI.refreshCandidate(id);
      const d = res.data || {};

      // The endpoint declines rather than overwriting a good screening with a
      // failed one (unreadable CV, truncated response, no active positions).
      // Nothing changed, and the user has to be told WHY or the button looks
      // broken.
      if (!d.refreshed) {
        toast(d.reason || 'Nothing was changed.', { icon: 'ℹ️', duration: 8000 });
        setRefreshResult((p) => ({ ...p, [id]: d }));
        return;
      }

      const oldScore = d.changes?.screening_score?.old;
      const newScore = d.changes?.screening_score?.new;
      const flipped  = d.tier?.old !== d.tier?.new;
      toast.success(
        `Score ${oldScore ?? '—'}% → ${newScore ?? '—'}%` +
        (flipped ? ` · verdict ${d.tier?.old ?? 'none'} → ${d.tier?.new ?? 'none'}` : ''),
        { duration: flipped ? 8000 : 4000 },
      );
      setRefreshResult((p) => ({ ...p, [id]: d }));
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Refresh failed');
    } finally {
      setRefreshing((p) => ({ ...p, [id]: false }));
    }
  };

  // ── Send the tier email for this row's CURRENT score ───────────────────
  // The only route to an interview- or rejection-tier email for a candidate who
  // is already in this table: the review modal is fed by screen-preview, which
  // only ever screens UNSCREENED documents, so an existing candidate can never
  // appear in it. Before this button, Rescreen was the only path — and it
  // re-derived the tier and rewrote workflow state to get there.
  //
  // No tier picker, deliberately. The tier is whatever the current score
  // implies; a picker would rebuild the ability to send an email that
  // contradicts the score. If HR wants a different outcome the route is to fix
  // the position or the criteria and refresh.
  const sendTierEmail = async (c, tier) => {
    const label = TIER_META[tier]?.label || tier;
    const to = (c.email || '').trim();
    const lines = [];

    // WHAT THIS PERSON WAS ALREADY TOLD — first, and only when it contradicts
    // what is about to go out.
    //
    // This is the 82 -> 45 case: a candidate emailed an interview invitation,
    // re-scored down to rejection tier, now one click from a rejection. The
    // bulk guard does not cover it — that guard only ever sees freshly screened
    // rows — so this dialog is the last thing between the two emails.
    //
    // Silent when the tiers agree and when nothing was ever sent. A warning
    // that fires on every send is a warning nobody reads, and most sends from
    // this button are the first contact the candidate has had.
    const priorTier = c.email_sent_type && c.email_sent_type !== 'none'
      ? c.email_sent_type : null;
    const contradicts = priorTier && priorTier !== tier;
    if (contradicts) {
      const priorLabel = TIER_META[priorTier]?.label || priorTier;
      const when = c.email_sent_at
        ? new Date(c.email_sent_at).toLocaleDateString(undefined,
            { day: 'numeric', month: 'short' })
        : null;
      lines.push(
        `⚠️ ALREADY CONTACTED — this candidate was sent a ${priorLabel} email` +
        (when ? ` on ${when}` : '') + '.',
        `Sending the ${label} email now will contradict it.`,
        '',
      );
    }

    // Name the ACTUAL recipient. "this candidate" is what let a forwarding
    // address go unnoticed; the address is the thing the person needs to check.
    lines.push(
      `Send the ${label} email to ${c.name}?`,
      '',
      `Recipient: ${to || '(no address on file)'}`,
      `Tier: ${label} — from the current score of ${c.screening_score ?? '—'}%`,
    );
    // A held row can still be sent per-candidate — that is the informed act the
    // bulk guard preserves. It is only informed if the reason is on screen
    // BEFORE the click, so it goes in the dialog rather than a chip elsewhere.
    if (c.score_hold_reason) {
      lines.push('', `⚠️ ${c.score_hold_reason}`);
    }
    if (!window.confirm(lines.join('\n'))) return;

    setSendingTier((p) => ({ ...p, [c.id]: true }));
    try {
      await cvScreeningAPI.sendScreenedEmail(c.id, tier, c.email);
      toast.success(`${label} email sent to ${to}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingTier((p) => ({ ...p, [c.id]: false }));
    }
  };

  const sendForm = async (candidateId) => {
    setSendingForm((p) => ({ ...p, [candidateId]: true }));
    try {
      await formsAPI.sendForm(candidateId);
      toast.success('Form invitation sent');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send form');
    } finally {
      setSendingForm((p) => ({ ...p, [candidateId]: false }));
    }
  };

  const sendScheduleLink = async (candidateId) => {
    setSendingSchedule((p) => ({ ...p, [candidateId]: true }));
    try {
      await interviewsAPI.sendScheduleLink(candidateId);
      toast.success('Interview scheduling link sent');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send scheduling link');
    } finally {
      setSendingSchedule((p) => ({ ...p, [candidateId]: false }));
    }
  };

  /**
   * Mint an on-site English assessment link and show it.
   *
   * Does NOT reload(). Minting changes nothing a row renders — the result chip
   * only appears once the candidate submits, which happens minutes later on a
   * different machine. A reload here would flip the list's loading flag and
   * unmount the modal the moment it opened, the same trap documented on
   * runScreen and LinkedSetPanel.
   *
   * The backend 403s when the candidate has no position or the position has no
   * bank configured, and its `detail` explains which. Surfaced verbatim rather
   * than replaced with a generic failure: "This position has no English
   * assessment configured" tells HR exactly where to go, and a bare "Failed"
   * does not.
   */
  const startEnglishTest = async (candidate) => {
    setMintingTest((p) => ({ ...p, [candidate.id]: true }));
    try {
      const res = await assessmentsAPI.mintToken(candidate.id);
      setTestLink({ candidate, data: res.data });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create the assessment link');
    } finally {
      setMintingTest((p) => ({ ...p, [candidate.id]: false }));
    }
  };

  const clearFilters = () => { setPosFilter('all'); setStatusFilter('all'); setExamFilter('all'); setSearch(''); };

  // ── Unscreened CV intake (manual screen-now) ──────────────────────────
  const toggleDoc = (id) => {
    setSelectedDocs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllDocs = () => {
    setSelectedDocs((prev) =>
      prev.size === unscreened.length ? new Set() : new Set(unscreened.map((d) => d.id))
    );
  };
  const runScreen = async (payload, label) => {
    setScreeningNow(true);
    try {
      const submittedIds = new Set(payload.document_ids || []);
      // Always score-only, never send. These buttons are clicked by a human who
      // is right here, so they open the review panel and let HR decide who gets
      // emailed. They deliberately do NOT consult auto_screening_enabled: that
      // flag governs the UNATTENDED email-import path (_process_email_cv), and
      // reading it here meant "Screen All" silently emailed every unscreened CV
      // in departments where it was on, with no preview and no confirmation.
      const res = await cvScreeningAPI.screenPreview(payload);
      const { results = [], screened = 0, skipped = 0 } = res.data || {};
      toast.success(`${label}: ${screened} screened${skipped ? `, ${skipped} skipped` : ''}`);
      setUnscreened((prev) => prev.filter((d) => !submittedIds.has(d.id)));
      setSelectedDocs(new Set());
      setPreviewResults(results);
      setShowPreview(results.length > 0);
      // NOTE: do NOT call reload() here. reload() flips the parent's `loading`
      // flag, which unmounts CandidatesTab (and this modal's local state) while
      // the spinner shows — the modal would never appear. The candidate list is
      // re-synced when the modal closes (onClose / sendAll).
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Screening failed');
    } finally {
      setScreeningNow(false);
    }
  };
  const screenSelected = () => {
    if (selectedDocs.size === 0) return;
    runScreen({ document_ids: Array.from(selectedDocs) }, 'Selected CVs');
  };
  const screenAll = () => {
    if (unscreened.length === 0) return;
    runScreen({ document_ids: unscreened.map((d) => d.id) }, 'All unscreened CVs');
  };

  // ── HR Review: per-candidate send from preview panel ─────────────────
  const sendFromPreview = async (candidateId, tier) => {
    setSendingPreview((s) => ({ ...s, [candidateId]: true }));
    try {
      // Use the HR-corrected email from the preview row (falls back to the
      // extracted address the backend already has when left unchanged).
      const cand = previewResults.find((r) => r.candidate_id === candidateId);
      await cvScreeningAPI.sendScreenedEmail(candidateId, tier, cand?.email);
      toast.success('Email sent');
      // Drop only this candidate from the modal; the parent list re-syncs when
      // the modal closes. Calling reload() here would unmount the modal mid-review.
      setPreviewResults((prev) => prev.filter((r) => r.candidate_id !== candidateId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    } finally {
      setSendingPreview((s) => ({ ...s, [candidateId]: false }));
    }
  };

  const skipFromPreview = (candidateId) => {
    setPreviewResults((prev) => prev.filter((r) => r.candidate_id !== candidateId));
  };

  const sendAllRecommended = async () => {
    if (previewResults.length === 0) return;
    setSendingAll(true);
    try {
      // Build the batch from sendable rows ONLY. Filtering the underlying list
      // here — not just hiding a section — is what makes the exclusion real:
      // an unreadable candidate has no tier to submit and is never enumerated.
      // The backend refuses them independently, so this is belt and braces.
      //
      // score_hold_reason is the exception to "belt and braces": the backend
      // does NOT refuse it, deliberately, because it is display-only and must
      // leave the form and schedule links open. So this filter is the ONLY
      // thing standing between a held row and a bulk rejection — without it,
      // Send All would re-introduce through the batch exactly the send the
      // pipeline guard exists to stop. Per-candidate Reject is unaffected.
      const items = previewResults
        // refreshedSinceLastEmail is the SECOND guard this filter carries that
        // the backend does not enforce, alongside score_hold_reason. A row
        // re-scored after the candidate was last emailed has moved underneath
        // what they were told — the 82 -> 45 case, where a bulk sweep would
        // send a rejection to someone holding an interview invitation. A row
        // never emailed has email_sent_at null and stays in; see the predicate.
        .filter((r) => !r.unreadable && r.tier && !r.score_hold_reason
                    && !refreshedSinceLastEmail(r))
        .map((r) => ({ candidate_id: r.candidate_id, tier: r.tier, email: r.email }));
      if (items.length === 0) {
        toast('Nothing to send — every remaining CV is unreadable.', { icon: 'ℹ️' });
        setSendingAll(false);
        return;
      }
      const res = await cvScreeningAPI.sendScreenedEmailsBatch(items);
      const { sent = 0, failed = 0 } = res.data || {};
      toast.success(`Sent ${sent} email${sent !== 1 ? 's' : ''}${failed ? `, ${failed} failed` : ''}`);
      setPreviewResults([]);
      setShowPreview(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Batch send failed');
    } finally {
      setSendingAll(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString(); } catch { return '—'; }
  };

  return (
    <div className="space-y-3">

      {/* ── Unscreened CVs (manual screening) ──────────────────────────── */}
      {unscreened.length > 0 && (
        <div className="bg-white rounded-2xl border border-amber-200 overflow-hidden">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-amber-800">Unscreened CVs</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                {unscreened.length} waiting
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={screenSelected}
                disabled={screeningNow || selectedDocs.size === 0 || !canAdd}
                title={canAdd ? '' : "🔒 You don't have permission"}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-gray-900 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {screeningNow ? 'Screening…' : `Screen Selected${selectedDocs.size ? ` (${selectedDocs.size})` : ''}`}
              </button>
              <button
                onClick={screenAll}
                disabled={screeningNow || !canAdd}
                title={canAdd ? '' : "🔒 You don't have permission"}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {screeningNow ? 'Screening…' : 'Screen All Unscreened'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-4 py-2 w-10">
                    <input
                      type="checkbox"
                      checked={unscreened.length > 0 && selectedDocs.size === unscreened.length}
                      onChange={toggleAllDocs}
                    />
                  </th>
                  <th className="text-left px-4 py-2 font-semibold">CV File</th>
                  <th className="text-left px-4 py-2 font-semibold">From</th>
                  <th className="text-left px-4 py-2 font-semibold">Received</th>
                  <th className="text-left px-4 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {unscreened.map((d) => (
                  <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selectedDocs.has(d.id)}
                        onChange={() => toggleDoc(d.id)}
                      />
                    </td>
                    <td className="px-4 py-2 font-medium text-gray-700">{d.original_filename}</td>
                    <td className="px-4 py-2 text-gray-500">{d.source_email_from || '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{fmtDate(d.created_at)}</td>
                    <td className="px-4 py-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Not screened</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Filters (only shown when there is data) ───────────────── */}
      {candidates.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200 px-5 py-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 items-end">

          {/* Search by name / email */}
          <div className="flex min-w-0 flex-col gap-1.5">
            <label htmlFor="candidate-search" className="text-xs font-semibold text-gray-600">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                id="candidate-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or email…"
                className="w-full h-11 pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XCircleIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {[
            { label: 'Position', value: posFilter, set: setPosFilter, all: 'All Positions', options: uniquePositions.map(value => [value, value]) },
            { label: 'Candidate Status', value: statusFilter, set: setStatusFilter, all: 'All Statuses', options: uniqueStatuses.map(value => [value, value.replace(/_/g, ' ')]) },
            { label: 'Exam Status', value: examFilter, set: setExamFilter, all: 'All Exam Statuses', options: Object.entries(EXAM_STATUS_LABELS) },
          ].map(filter => (
            <label key={filter.label} className="flex min-w-0 flex-col gap-1.5 text-xs font-semibold text-gray-600">
              {filter.label}
              <select aria-label={filter.label} value={filter.value} onChange={event => filter.set(event.target.value)}
                className="w-full min-w-0 h-11 px-3 text-sm font-normal text-gray-800 bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:border-green-500">
                <option value="all">{filter.all}</option>
                {filter.options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          ))}
        </div>
      )}

      {/* ── Candidate table ────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold text-gray-800">Candidates</h2>
            {(posFilter !== 'all' || statusFilter !== 'all' || examFilter !== 'all' || search.trim()) && (
              <>
                <span className="text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full">
                  {filtered.length} of {candidates.length}
                </span>
                <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline">
                  clear
                </button>
              </>
            )}
          </div>
          <button onClick={reload} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
            <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {candidates.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <p className="text-4xl mb-2">📋</p>
            <p className="text-sm">No candidates yet. Connect an email account in Email Settings to start receiving CVs.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <p className="text-sm">No candidates match the selected filters.</p>
            <button onClick={clearFilters} className="mt-2 text-xs text-green-600 hover:underline">
              Clear filters
            </button>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                  <th className="text-left px-4 py-2.5 font-semibold">Candidate</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Position Matched</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Score</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Status</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Email Sent</th>
                  <th className="text-left px-4 py-2.5 font-semibold">Form</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {paginatedCandidates.map((c) => (
                  <tr
                    key={c.id}
                    className={`border-b border-gray-50 hover:bg-gray-50/50 ${
                      c.screening_rejected ? 'bg-red-50/40 opacity-70' : ''
                    }`}
                  >

                    {/* Candidate info — full detail lives in the modal (click name) */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setDetailCandidate(c)}
                          className="font-medium text-gray-800 hover:text-green-700 hover:underline text-left"
                          title="View candidate details"
                        >
                          {c.name}
                        </button>
                        {c.linked_count > 0 && (
                          <button
                            onClick={() => setLinkPanelCandidate(c)}
                            title={c.linked_needs_review
                              ? 'Possibly the same person as another row - needs review'
                              : 'Linked to another row as the same person'}
                            className={`px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide ${
                              c.linked_needs_review
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-blue-100 text-blue-800'
                            }`}
                          >
                            {c.linked_needs_review ? 'REVIEW' : 'LINKED'} {c.linked_count}
                          </button>
                        )}
                        {c.screening_rejected && (
                          <span
                            className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 text-red-700 tracking-wide"
                            title="Disqualified — click the name for details"
                          >
                            ❌ REJECTED
                          </span>
                        )}
                      </div>
                      <CandidateEmailCell candidate={c} canEdit={canEdit} reload={reload} />
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${EXAM_STATUS_COLORS[examStatus(c)] || 'bg-gray-100 text-gray-700'}`}>
                        Exam: {EXAM_STATUS_LABELS[examStatus(c)] || examStatus(c)}
                      </span>
                      {/* Renders nothing at all when the candidate has no
                          submitted assessment, which is most rows. */}
                      {c.english_assessment && (
                        <div className="mt-1">
                          <EnglishAssessmentChip candidate={c} />
                        </div>
                      )}
                      {/* One-line summary; the full reasons/flags/CV live in the modal */}
                      {(() => {
                        // Only the disqualifying ones drive the "n issues found"
                        // summary — a met criterion is not an issue.
                        const reasons = reasonLines(c.reject_reasons).filter((r) => r.disqualifying);
                        const flags = Array.isArray(c.screening_flags) ? c.screening_flags : [];
                        if (!reasons.length && !flags.length && !c.cv_original_filename) return null;
                        let summary;
                        if (reasons.length) {
                          const first = reasons[0].text;
                          summary = (
                            <>
                              <span className="text-red-600 font-medium">
                                {reasons.length} issue{reasons.length > 1 ? 's' : ''} found
                              </span>
                              <span className="text-gray-500">
                                {' — '}{first.length > 38 ? `${first.slice(0, 38)}…` : first}
                              </span>
                            </>
                          );
                        } else if (flags.length) {
                          summary = (
                            <span className="text-gray-500">
                              {flags.length} screening flag{flags.length > 1 ? 's' : ''}
                            </span>
                          );
                        } else {
                          summary = <span className="text-gray-400">View details</span>;
                        }
                        return (
                          <button
                            onClick={() => setDetailCandidate(c)}
                            className="mt-1 block text-[11px] text-left hover:underline"
                            title="View all details"
                          >
                            {summary}
                          </button>
                        );
                      })()}
                    </td>

                    {/* Position — clicking sets the position filter */}
                    <td className="px-4 py-3">
                      <CandidatePositionCell
                        candidate={c}
                        positions={positions}
                        canEdit={canEdit}
                        reload={reload}
                        onFilter={(t) => setPosFilter(posFilter === t ? 'all' : t)}
                      />
                    </td>

                    {/* Score */}
                    <td className="px-4 py-3">
                      {c.screening_score != null ? (
                        <span className={`font-semibold ${
                          c.screening_score >= 80 ? 'text-green-600'
                            : c.screening_score >= 50 ? 'text-amber-600'
                              : 'text-red-500'
                        }`}>
                          {c.screening_score}%
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                      {/* Held, not blocked. score_hold_reason is display-only —
                          the form invite, the schedule link and HR's Reject
                          button all still work on this row. Amber rather than
                          the orange used for "cannot be sent to", so the two
                          do not read as the same severity. */}
                      {c.score_hold_reason && (
                        <div className="mt-1 inline-flex items-start gap-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 max-w-md"
                             title={c.score_hold_reason}>
                          <ExclamationCircleIcon className="w-3.5 h-3.5 shrink-0 mt-px" />
                          <span>Held — nothing disqualifying</span>
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1 items-start">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || ''}`}>
                          {c.status.replace(/_/g, ' ')}
                        </span>
                        {c.form_screening_override ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            ➡️ Overridden
                          </span>
                        ) : FORM_SCREENING_BADGE[c.form_screening_status] && (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FORM_SCREENING_BADGE[c.form_screening_status].color}`}>
                            {FORM_SCREENING_BADGE[c.form_screening_status].label}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Email sent */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className={`text-xs font-medium ${EMAIL_TYPE_BADGE[c.email_sent_type]?.color || 'text-gray-400'}`}>
                          {EMAIL_TYPE_BADGE[c.email_sent_type]?.label || '—'}
                        </span>
                        {c.email_sent_at && (
                          <span className="text-xs text-gray-400">
                            {new Date(c.email_sent_at).toLocaleString()}
                          </span>
                        )}
                        {c.email_error && (
                          <span className="text-xs text-red-500 max-w-[12rem] truncate" title={c.email_error}>
                            ⚠ {c.email_error}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Form status */}
                    <td className="px-4 py-3">
                      {c.has_form_submission ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">
                          ✅ Submitted
                        </span>
                      ) : c.form_sent_at ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs font-medium text-blue-600">📋 Sent</span>
                          <span className="text-xs text-gray-400">{new Date(c.form_sent_at).toLocaleDateString()}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* On-site English test. Deliberately NOT hidden behind
                            sendBlocked below: that flag is about whether the
                            candidate can be SENT to (a bad address, an unset
                            position), and this button sends nothing at all —
                            it mints a link HR carries to a laptop in the same
                            room. A candidate with an unusable email address can
                            still sit an assessment.

                            HIDDEN, not disabled, when the row's position has no
                            bank. A disabled button with a tooltip is still an
                            invitation to try, and the answer would never change
                            by trying — the fix is on the POSITION, not the
                            candidate. Two of the row's controls are already
                            disabled-with-a-reason (sendBlocked, canEdit) and
                            those are worth explaining because HR can act on
                            them here.

                            english_test_available is not a second source of
                            truth: the server derives it on every read from the
                            same column the token endpoint tests, so it cannot
                            drift. The endpoint's own refusal STAYS as the
                            backstop — the bank can be cleared between this page
                            loading and the click, and there is a 409 for a
                            position naming a bank that no longer exists, which
                            this flag cannot see. startEnglishTest surfaces
                            both. */}
                        {c.english_test_available && (
                          <button
                            onClick={() => startEnglishTest(c)}
                            disabled={!!mintingTest[c.id] || !canEdit}
                            title={canEdit
                              ? 'Create an on-site English assessment link'
                              : "🔒 You don't have permission"}
                            className="flex items-center gap-1 text-xs px-2.5 py-1 border border-violet-200 text-violet-700 rounded-lg hover:bg-violet-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <AcademicCapIcon className="w-3 h-3" />
                            {mintingTest[c.id] ? '…' : 'English Test'}
                          </button>
                        )}
                        {/* manual_chat candidates (created from AI Chat interview-email
                            flow) skip the info form — they go straight to scheduling. */}
                        {(() => {
                          const isManualChatInvite =
                            c.source === 'manual_chat' && !c.has_form_submission;
                          // Interview tier means we have already decided to talk to
                          // them, so scheduling must not wait on a form round-trip.
                          // Keyed on email_sent_type rather than status: status moves
                          // on (form_sent, schedule_sent…) while email_sent_type records
                          // the tier decision permanently. The details tier never sets
                          // it — step 8 skips that tier — so this cannot widen to
                          // details, rejection or unscreened candidates.
                          const isInterviewTier = c.email_sent_type === 'interview';
                          // Both buttons start a send the backend will refuse when a
                          // block applies, so they are hidden on the same condition the
                          // tier button below already uses — the reasons come from
                          // send_policy / recipient_policy, which is what actually
                          // refuses. Before this, a closed (no_response) candidate still
                          // showed "Resend Form", and clicking it set the status back to
                          // form_sent: the close undone, with nothing recording that one
                          // had happened. An unmatched candidate had the same button and
                          // it 409'd, so this fixes that inconsistency in the same move.
                          //
                          // Reading the reason rather than testing the status keeps the
                          // button and the refusal on ONE source of truth: a reason added
                          // to the resolver later hides these buttons with no change here.
                          const sendBlocked = !!(c.position_block_reason || c.email_blocked_reason);
                          const showSchedule = !sendBlocked
                            && (c.has_form_submission || isManualChatInvite || isInterviewTier);
                          // Unchanged on purpose: an interview-tier candidate still gets
                          // "Send Form", which is now the only way they receive one.
                          const showForm = !sendBlocked
                            && !c.has_form_submission && !isManualChatInvite;
                          // "Resend" once a schedule link has already gone out.
                          const scheduleResent =
                            c.status === 'schedule_sent' || !!c.schedule_token;
                          return (
                            <>
                              {showForm && (
                                <button
                                  onClick={() => sendForm(c.id)}
                                  disabled={!!sendingForm[c.id]}
                                  title="Send form invitation"
                                  className="flex items-center gap-1 text-xs px-2.5 py-1 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50 disabled:opacity-50"
                                >
                                  <PaperAirplaneIcon className="w-3 h-3" />
                                  {sendingForm[c.id] ? '…' : c.form_sent_at ? 'Resend' : 'Send Form'}
                                </button>
                              )}
                              {showSchedule && (
                                <button
                                  onClick={() => sendScheduleLink(c.id)}
                                  disabled={!!sendingSchedule[c.id] || !canSchedule}
                                  title={canSchedule ? 'Send interview scheduling link' : "🔒 You don't have permission"}
                                  className="flex items-center gap-1 text-xs px-2.5 py-1 border border-cyan-200 text-cyan-700 rounded-lg hover:bg-cyan-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <CalendarDaysIcon className="w-3 h-3" />
                                  {sendingSchedule[c.id] ? '…' : scheduleResent ? 'Resend Schedule' : 'Send Schedule'}
                                </button>
                              )}
                            </>
                          );
                        })()}
                        {/* Send the tier email for the CURRENT score.
                            Hidden while the candidate is awaiting form review:
                            that tab has its own Reject, and shipping both would
                            put two controls that send the same email on the same
                            person. awaitingFormReview() is the same predicate
                            FormReviewTab builds its list from, so the two cannot
                            drift apart. Also hidden with no tier (never scored,
                            or a screening that failed) and while a block applies
                            — the backend refuses those independently, so showing
                            the button would only promise a send that 400s. */}
                        {(() => {
                          const tier = candidateTier(c);
                          if (!tier) return null;
                          if (awaitingFormReview(c)) return null;
                          if (c.unreadable || c.status === 'unreadable_cv') return null;
                          if (c.position_block_reason || c.email_blocked_reason) return null;
                          const meta = TIER_META[tier];
                          return (
                            <button
                              onClick={() => sendTierEmail(c, tier)}
                              disabled={!!sendingTier[c.id] || !canEdit}
                              title={canEdit
                                ? `Send the ${meta.label} email for the current score`
                                : "🔒 You don't have permission"}
                              className={`flex items-center gap-1 text-xs px-2.5 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${meta.color}`}
                            >
                              <EnvelopeIcon className="w-3 h-3" />
                              {sendingTier[c.id] ? '…' : `Send ${meta.label}`}
                            </button>
                          );
                        })()}
                        {/* Refresh, not Rescreen. Recomputes the score only —
                            it cannot email anyone and cannot move workflow
                            state. The sending rescreen is gone from the UI; its
                            endpoint is still live but nothing calls it. */}
                        <button
                          onClick={() => refresh(c.id)}
                          disabled={!!refreshing[c.id] || !canEdit}
                          title={canEdit
                            ? 'Re-score against the current criteria. Sends nothing.'
                            : "🔒 You don't have permission"}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ArrowPathIcon className="w-3 h-3" />
                          {refreshing[c.id] ? '…' : 'Refresh'}
                        </button>
                      </div>
                      {/* Outcome of the last refresh on this row. */}
                      {refreshResult[c.id] && (
                        <RefreshOutcome
                          result={refreshResult[c.id]}
                          positions={positions}
                          candidate={c}
                          reload={reload}
                          canEdit={canEdit}
                          onDismiss={() => setRefreshResult((p) => {
                            const n = { ...p }; delete n[c.id]; return n;
                          })}
                        />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination controls ──────────────────────────────────────── */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 text-xs text-gray-500 flex-wrap">
            <span>
              Showing <span className="font-medium text-gray-700">{rangeStart}-{rangeEnd}</span> of{' '}
              <span className="font-medium text-gray-700">{filtered.length}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                ← Prev
              </button>
              <span className="text-gray-600">
                Page <span className="font-medium text-gray-800">{page}</span> of{' '}
                <span className="font-medium text-gray-800">{totalPages}</span>
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-2.5 py-1 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
          </>
        )}
      </div>

      {/* ── HR Review Preview Modal ────────────────────────────────────── */}
      <ScreeningPreviewModal
        open={showPreview}
        results={previewResults}
        sendingAll={sendingAll}
        sendingOne={sendingPreview}
        onSend={sendFromPreview}
        onSkip={skipFromPreview}
        onSendAll={sendAllRecommended}
        onOpenCv={openCv}
        onOpenCvTab={openCvInTab}
        onEmailChange={(candidateId, email) =>
          setPreviewResults((prev) =>
            prev.map((r) => (r.candidate_id === candidateId ? { ...r, email } : r))
          )
        }
        onClose={() => { setShowPreview(false); setPreviewResults([]); reload(); }}
      />

      {/* Linked-set panel. Mounted HERE, on the parent, not inside the row:
          reload() flips `loading` and unmounts children, so a panel owned by a
          row would disappear mid-action. reload() is called on CLOSE only, once
          the user is finished, never while the panel is open. */}
      <Modal
        open={!!linkPanelCandidate}
        onClose={() => { setLinkPanelCandidate(null); reload(); }}
        title={linkPanelCandidate ? `Same person as ${linkPanelCandidate.name}?` : 'Linked rows'}
        size="lg"
      >
        {linkPanelCandidate && (
          <LinkedSetPanel candidate={linkPanelCandidate} />
        )}
      </Modal>

      {/* ── PDF Preview Modal ──────────────────────────────────────────── */}
      <Modal open={pdfModal.open} onClose={closePdf} title={pdfModal.filename || 'CV Preview'} size="xl">
        <div className="border border-gray-200 rounded-xl overflow-hidden -mx-2">
          {pdfModal.url && (
            <iframe
              src={pdfModal.url}
              className="w-full block"
              style={{ height: '74vh', border: 'none' }}
              title="CV Preview"
            />
          )}
        </div>
      </Modal>

      {/* ── Minted English assessment link ─────────────────────────────── */}
      {testLink && (
        <AssessmentLinkModal
          candidate={testLink.candidate}
          data={testLink.data}
          onClose={() => setTestLink(null)}
        />
      )}

      {/* ── Candidate Detail Modal (full reasons / flags / CV) ─────────── */}
      <Modal
        open={!!detailCandidate}
        onClose={() => setDetailCandidate(null)}
        title={detailCandidate ? detailCandidate.name : 'Candidate'}
        size="lg"
      >
        {detailCandidate && (() => {
          const c = detailCandidate;
          const reasons = reasonLines(c.reject_reasons);
          const flags = Array.isArray(c.screening_flags) ? c.screening_flags : [];
          return (
            <div className="space-y-4 text-sm">
              {/* Context: position / score / status */}
              <div className="flex flex-wrap items-center gap-2">
                {c.matched_position_title ? (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
                    {c.matched_position_title}
                  </span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500 italic">
                    {NO_MATCH}
                  </span>
                )}
                {c.screening_score != null && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    c.screening_score >= 80 ? 'bg-green-50 text-green-700'
                      : c.screening_score >= 50 ? 'bg-amber-50 text-amber-700'
                        : 'bg-red-50 text-red-600'
                  }`}>
                    Score {c.screening_score}%
                  </span>
                )}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[c.status] || 'bg-gray-100 text-gray-700'}`}>
                  {c.status.replace(/_/g, ' ')}
                </span>
                {c.screening_rejected && (
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">❌ REJECTED</span>
                )}
              </div>

              {c.email && (
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Email</span>
                  <div className="text-gray-700">{c.email}</div>
                </div>
              )}

              {/* Every hard criterion the screening checked — failures and
                  passes both. The count in the heading is of DISQUALIFIERS, so
                  it cannot be inflated by criteria the candidate cleared. */}
              {reasons.length > 0 && (() => {
                const failed = reasons.filter((r) => r.disqualifying);
                return (
                  <div>
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                      Criteria checked ({failed.length} disqualifier{failed.length === 1 ? '' : 's'})
                    </span>
                    <ul className="mt-1 space-y-1">
                      {reasons.map((r, i) => (
                        <li
                          key={i}
                          className={`leading-snug ${r.disqualifying ? 'text-red-600' : 'text-gray-500'}`}
                        >
                          {r.disqualifying ? '•' : '✓'} {r.text}
                          {r.off && (
                            <span className="ml-1 text-xs text-amber-700">
                              (not in this position's criteria)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })()}

              {/* Screening checks — full notes (incl. Location confirmation) */}
              {flags.length > 0 && (
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    Screening Checks ({flags.length})
                  </span>
                  <ul className="mt-1 space-y-1">
                    {flags.map((f, i) => (
                      <li key={i} className="flex gap-2 leading-snug">
                        <span className="shrink-0">{f.status}</span>
                        <span className="text-gray-700">
                          <span className="font-medium">{f.check}</span>
                          {f.note && <span className="text-gray-500"> — {f.note}</span>}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CV link — opens the existing PDF preview modal */}
              {c.cv_original_filename && (
                <div>
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">CV</span>
                  <div>
                    <button
                      onClick={() => { setDetailCandidate(null); openCv(c.id, c.cv_original_filename); }}
                      disabled={!!loadingPdf[c.id]}
                      className="mt-0.5 text-blue-600 hover:text-blue-800 hover:underline disabled:opacity-50 text-left"
                      title={`Preview ${c.cv_original_filename}`}
                    >
                      {loadingPdf[c.id] ? '⏳ Loading…' : `📄 ${c.cv_original_filename}`}
                    </button>
                  </div>
                </div>
              )}

              {reasons.length === 0 && flags.length === 0 && !c.cv_original_filename && (
                <p className="text-gray-400">No additional screening details for this candidate.</p>
              )}
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}


/* ───────────────────── Screening Preview Modal ──────────────────── */

const TIER_INFO = {
  interview: { label: 'Interview Track', color: 'bg-green-100 text-green-700 border-green-300',  dot: '🟢' },
  details:   { label: 'Request Details',  color: 'bg-amber-100 text-amber-700 border-amber-300',  dot: '🟡' },
  rejection: { label: 'Reject',           color: 'bg-red-100 text-red-700 border-red-300',        dot: '🔴' },
};

function ScreeningPreviewModal({
  open, results, sendingAll, sendingOne, onSend, onSkip, onSendAll, onEmailChange,
  onOpenCv, onOpenCvTab, onClose,
}) {
  if (!open) return null;

  // Partition, don't just style differently. `sendable` is the list every send
  // action iterates; an unreadable CV is never in it, so no bulk action can
  // reach one. They were never scored, so they have no tier to act on.
  // A blocked recipient is excluded from `sendable` for the same reason an
  // unreadable CV is: the list every send action iterates must contain only
  // rows that can actually be sent. The backend refuses them independently.
  // `r.tier` is already the exclusion for a failed screening: the server sends
  // tier=null for a NULL score, so these rows drop out of `sendable` here with
  // no second filter, and the "Send All Recommended (n)" count excludes them.
  // score_hold_reason also drops out of `sendable`, and that is the point: the
  // pipeline withheld the automatic rejection so a person would read the row,
  // and "Send All Recommended" is the opposite of a person reading the row.
  // It is NOT a block — the per-candidate Reject button, the form invite and
  // the schedule link all still work on these rows. It only keeps them out of
  // the bulk action.
  // A refreshed row drops out too, for the same reason a held one does: its
  // score moved after the candidate was last told something, so a bulk sweep
  // would act on a verdict nobody has read. Per-candidate sending stays open.
  // This filter and the batch builder in sendAllRecommended are SEPARATE code
  // paths over the same rule — the batch does not read this const — so both
  // carry the guard and a test pins both as source.
  const sendable   = results.filter((r) => !r.unreadable && r.tier
                                        && !r.unmatched_reason && !r.email_blocked_reason
                                        && !r.score_hold_reason
                                        && !refreshedSinceLastEmail(r));
  const unreadable = results.filter((r) => r.unreadable);
  // Not-scored is partitioned BEFORE unmatched, extending the same rule one
  // step: the problem that has to be fixed first owns the row. Every failed
  // screening also has no position, so without this it would be reported under
  // "No matched position" — a panel that says these candidates "were screened
  // and scored" and offers setting the position as the remedy. Both halves
  // would be wrong. Setting a position on a row that was never scored produces
  // a sendable row with no basis for the tier it would be sent.
  const notScored  = results.filter((r) => !r.unreadable && r.score == null);
  // Unmatched is partitioned BEFORE blocked-recipient so a candidate that is
  // both appears once, under the problem that has to be fixed first: without a
  // position there is nothing correct to send, whatever the address says.
  const unmatched  = results.filter((r) => !r.unreadable && r.score != null
                                        && r.unmatched_reason);
  const blockedRcpt = results.filter((r) => !r.unreadable && r.score != null
                                         && !r.unmatched_reason
                                         && r.email_blocked_reason);
  // Last in precedence, deliberately: this is the only one of the four that is
  // not a blocker. A row with no position or a bad address has something that
  // must be FIXED first and belongs under that heading; a score-held row is
  // otherwise ready and is waiting on a decision.
  const scoreHeld  = results.filter((r) => !r.unreadable && r.score != null
                                        && !r.unmatched_reason
                                        && !r.email_blocked_reason
                                        && r.score_hold_reason);

  return (
    <Modal open={open} onClose={onClose} title={`Screening Results (${results.length} candidate${results.length !== 1 ? 's' : ''})`} size="xl">
      {/* Header actions */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-gray-500">
          No emails have been sent. Review each candidate and choose an action.
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50"
          >
            Skip All
          </button>
          <button
            onClick={onSendAll}
            disabled={sendingAll || sendable.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            {sendingAll ? 'Sending…' : `Send All Recommended (${sendable.length})`}
          </button>
        </div>
      </div>

      {/* ── Held: low score, nothing disqualifying — a decision, not a block ── */}
      {scoreHeld.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-800">
              Held for your read ({scoreHeld.length}) — low score, nothing disqualifying
            </span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-amber-800">
              These candidates scored below the rejection threshold, but the screening found
              no criterion they actually fail — so the rejection email was not sent
              automatically. Nothing here is blocked: you can still send the form, send the
              interview link, or reject them deliberately. They are only kept out of
              “Send All Recommended”, because that is a bulk action and this is a judgement.
            </p>
            {scoreHeld.map((r) => (
              <div
                key={r.candidate_id}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-amber-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {r.name && r.name !== 'Pending Screening' ? r.name : (r.cv_filename || 'Unnamed CV')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {r.email || 'no address extracted'}
                    {r.cv_filename && <> · 📄 {r.cv_filename}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">
                    {r.score}%
                  </span>
                  <span className="text-xs text-gray-500">no disqualifying reason</span>
                  <button
                    onClick={() => onOpenCv?.(r.candidate_id, r.cv_filename)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="w-3 h-3" />
                    Open CV
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Not scored — screening ran and produced nothing ──────────────── */}
      {notScored.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-800">
              Not scored ({notScored.length}) — screening did not complete
            </span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-amber-800">
              Screening ran for these candidates but produced no score — the response was
              truncated, or it could not be read. There is no score, which is not the same
              as a score of zero: nothing here is a judgement about the candidate. They have
              no tier, so no email can be sent and they are excluded from Send All
              Recommended. Re-screen them; setting a position will not help, because the
              score they would be sent on does not exist.
            </p>
            {notScored.map((r) => (
              <div
                key={r.candidate_id}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-amber-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {r.name && r.name !== 'Pending Screening' ? r.name : (r.cv_filename || 'Unnamed CV')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {r.email || 'no address extracted'}
                    {r.cv_filename && <> · 📄 {r.cv_filename}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-semibold">
                    no score
                  </span>
                  <button
                    onClick={() => onOpenCv?.(r.candidate_id, r.cv_filename)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="w-3 h-3" />
                    Open CV
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── No matched position — scored, but nothing can be sent ────────── */}
      {unmatched.length > 0 && (
        <div className="mb-4 border border-amber-200 bg-amber-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-amber-200 flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm font-semibold text-amber-800">
              No matched position ({unmatched.length}) — cannot be sent to
            </span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-amber-800">
              These candidates were screened and scored, but the AI matched them to no position.
              Every template names the role and the company, so an email would read
              “the role at {'{'}our company{'}'}” — and the form would carry only the general
              questions while the interview link would offer no dates. The server refuses these
              sends. Set the position on the row in the Candidates table and they become sendable.
              The score is shown because it is what tells you whether that is worth doing.
            </p>
            {unmatched.map((r) => (
              <div
                key={r.candidate_id}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-amber-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {r.name && r.name !== 'Pending Screening' ? r.name : (r.cv_filename || 'Unnamed CV')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {r.email || 'no address extracted'}
                    {r.cv_filename && <> · 📄 {r.cv_filename}</>}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {r.score != null && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 font-semibold">
                      {r.score}%
                    </span>
                  )}
                  {r.tier && (
                    <span className="text-xs text-gray-500">would be {r.tier}</span>
                  )}
                  <button
                    onClick={() => onOpenCv?.(r.candidate_id, r.cv_filename)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="w-3 h-3" />
                    Open CV
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Unreadable CVs — no score, no tier, no send action ───────────── */}
      {unreadable.length > 0 && (
        <div className="mb-4 border border-orange-200 bg-orange-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-orange-200 flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-orange-600 shrink-0" />
            <span className="text-sm font-semibold text-orange-800">
              Unreadable CVs ({unreadable.length}) — cannot be screened
            </span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-orange-800">
              No text could be extracted from these files, so they were never scored against a
              position. They are <strong>not</strong> rejections — the CV could not be read, which
              says nothing about the candidate. Usually a scanned or photographed PDF, or a .docx
              whose content sits inside images. Open the CV to read it yourself, or ask the
              candidate for a text-based copy.
            </p>
            {unreadable.map((r) => (
              <div
                key={r.candidate_id}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-orange-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {r.name && r.name !== 'Pending Screening' ? r.name : (r.cv_filename || 'Unnamed CV')}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {r.email || 'no address extracted'}
                    {r.cv_filename && <> · 📄 {r.cv_filename}</>}
                  </p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => onOpenCv?.(r.candidate_id, r.cv_filename)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="w-3 h-3" />
                    Open CV
                  </button>
                  <button
                    onClick={() => onSkip(r.candidate_id)}
                    className="text-xs px-2.5 py-1 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50"
                  >
                    ⏭ Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Blocked recipients — screened fine, but the address is one of ours ── */}
      {blockedRcpt.length > 0 && (
        <div className="mb-4 border border-orange-200 bg-orange-50 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 border-b border-orange-200 flex items-center gap-2">
            <ExclamationCircleIcon className="w-4 h-4 text-orange-600 shrink-0" />
            <span className="text-sm font-semibold text-orange-800">
              Needs a real email ({blockedRcpt.length}) — cannot be sent to
            </span>
          </div>
          <div className="px-4 py-3 space-y-2">
            <p className="text-xs text-orange-800">
              These were screened normally, but the address on file is one of ours. CVs are
              forwarded from it, so no candidate address could be extracted. Correct the address
              on the Candidates tab — each row has an <strong>edit</strong> link — then send.
            </p>
            {blockedRcpt.map((r) => (
              <div
                key={r.candidate_id}
                className="flex items-center justify-between gap-3 flex-wrap bg-white border border-orange-200 rounded-lg px-3 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">
                    {r.name && r.name !== 'Pending Screening' ? r.name : (r.cv_filename || 'Unnamed CV')}
                    {typeof r.score === 'number' && (
                      <span className="ml-2 text-xs text-gray-500">{r.score}%</span>
                    )}
                  </p>
                  <p className="text-xs text-orange-700">{r.email_blocked_reason}</p>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Needed most here: the address on file is wrong, and the CV
                      is where the right one is if it is anywhere at all. */}
                  <button
                    onClick={() => onOpenCvTab?.(r.candidate_id)}
                    title="Open this CV in a new tab to find the candidate's own address"
                    className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                    Open CV
                  </button>
                  <button
                    onClick={() => onSkip(r.candidate_id)}
                    className="text-xs px-2.5 py-1 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50"
                  >
                    ⏭ Skip
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {sendable.length === 0 ? (
        <div className="text-center py-10 text-gray-400">
          <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="text-sm">
            {unreadable.length > 0
              ? 'No screenable candidates in this batch.'
              : 'All candidates have been actioned.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-[68vh] overflow-y-auto pr-1">
          {sendable.map((r) => {
            const tier = TIER_INFO[r.tier] || TIER_INFO.rejection;
            const scoreColor = r.score >= 80 ? 'text-green-600' : r.score >= 50 ? 'text-amber-600' : 'text-red-500';
            const isSending = !!sendingOne[r.candidate_id];
            return (
              <div key={r.candidate_id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Candidate header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap bg-gray-50/60">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{r.name}</span>
                      <span className={`text-lg font-bold ${scoreColor}`}>{r.score}%</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${tier.color}`}>
                        {tier.dot} {tier.label}
                      </span>
                      {r.rejected && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">❌ REJECTED</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <div className="flex items-center gap-1">
                        <EnvelopeIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                        <input
                          type="email"
                          value={r.email || ''}
                          onChange={(e) => onEmailChange(r.candidate_id, e.target.value)}
                          disabled={isSending}
                          placeholder="Enter email address"
                          title="Edit the recipient address before sending"
                          className="text-xs px-2 py-1 border border-gray-200 rounded-md w-60 max-w-full focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </div>
                      {r.cv_filename && <span className="text-xs text-gray-400">· 📄 {r.cv_filename}</span>}
                    </div>
                  </div>
                  {/* Action buttons */}
                  <div className="flex items-center gap-1.5 flex-wrap shrink-0">
                    {/* First, and separated from the send actions: this is the
                        check you make BEFORE choosing a tier, not another way
                        to act on the candidate. The address beside it was
                        auto-filled from this document. */}
                    <button
                      onClick={() => onOpenCvTab?.(r.candidate_id)}
                      title="Open this CV in a new tab to check the address against the document"
                      className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 mr-1"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      Open CV
                    </button>
                    <button
                      onClick={() => onSend(r.candidate_id, 'interview')}
                      disabled={isSending}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 border border-green-200 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-40"
                    >
                      <EnvelopeIcon className="w-3 h-3" />
                      {isSending ? '…' : 'Interview'}
                    </button>
                    <button
                      onClick={() => onSend(r.candidate_id, 'details')}
                      disabled={isSending}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50 disabled:opacity-40"
                    >
                      <EnvelopeIcon className="w-3 h-3" />
                      {isSending ? '…' : 'Details'}
                    </button>
                    <button
                      onClick={() => onSend(r.candidate_id, 'rejection')}
                      disabled={isSending}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-40"
                    >
                      <EnvelopeIcon className="w-3 h-3" />
                      {isSending ? '…' : 'Reject'}
                    </button>
                    <button
                      onClick={() => onSkip(r.candidate_id)}
                      disabled={isSending}
                      className="text-xs px-2.5 py-1 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                    >
                      ⏭ Skip
                    </button>
                  </div>
                </div>

                {/* Detail body */}
                <div className="px-4 py-3 space-y-2">
                  {/* Rejection reasons */}
                  {reasonLines(r.reject_reasons).length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-red-600 uppercase tracking-wide mb-1">
                        Criteria checked
                      </p>
                      <ul className="space-y-0.5">
                        {reasonLines(r.reject_reasons).map((rr, i) => (
                          <li
                            key={i}
                            className={`text-xs ${rr.disqualifying ? 'text-red-600' : 'text-gray-500'}`}
                          >
                            {rr.disqualifying ? '•' : '✓'} {rr.text}
                            {rr.off && (
                              <span className="ml-1 text-amber-700">
                                (not in this position's criteria)
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Notes */}
                  {r.notes && (
                    <p className="text-xs text-gray-600 italic">{r.notes}</p>
                  )}

                  {/* Flags */}
                  {r.flags?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Flags</p>
                      <div className="flex flex-wrap gap-1.5">
                        {r.flags.map((f, i) => (
                          <span
                            key={i}
                            title={f.note || f.check}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700"
                          >
                            <span>{f.status}</span>
                            <span className="truncate max-w-[10rem]">{f.check}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}


/* ─────────────────────────── Form Review ────────────────────────── */

// 80%+ → interview tier; 50-79% → details tier (matches backend tier_for_score)
const candidateTier = (c) => {
  if (c.screening_score == null) return null;
  if (c.screening_score >= 80) return 'interview';
  if (c.screening_score >= 50) return 'details';
  return 'rejection';
};

function FormReviewTab({ candidates, reload }) {
  const { canModule } = useAuth();
  const canOverride = canModule('hr_interview_scheduling', 'edit');
  const [sending, setSending] = useState({});
  // Optimistically hide candidates whose schedule invite was just sent so they
  // disappear from the list immediately; reload() then confirms via the backend.
  const [removedIds, setRemovedIds] = useState(() => new Set());

  // Anchor on the persisted state: a submitted candidate still sitting at
  // form_submitted (not yet overridden) is awaiting manual HR review. This
  // captures BOTH interview-tier held/review candidates AND details-tier
  // (collect-info) candidates, while excluding passed-interview (advanced to
  // schedule_sent) and already-overridden candidates. The pending_hr_review
  // clause also excludes overridden candidates — after Send Schedule Invite
  // sets form_screening_override, they must drop out on reload.
  // The membership test is awaitingFormReview(), shared with the Candidates
  // row, which hides its tier-send button for exactly this set. One predicate,
  // two consumers: without that, a candidate could carry a tier-send button in
  // one tab and a Reject button in the other, both sending the same email.
  const flagged = useMemo(
    () => candidates.filter((c) => !removedIds.has(c.id) && awaitingFormReview(c)),
    [candidates, removedIds],
  );

  const sendInvite = async (candidateId) => {
    setSending((s) => ({ ...s, [candidateId]: true }));
    try {
      await formsAPI.screeningOverride(candidateId);
      toast.success('Scheduling link sent');
      // Optimistic removal: drop the candidate from the list right away.
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.add(candidateId);
        return next;
      });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send scheduling link');
    } finally {
      setSending((s) => ({ ...s, [candidateId]: false }));
    }
  };

  if (flagged.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 text-center py-12 text-gray-400">
        <ShieldExclamationIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No candidates awaiting form review. Held and review-needed candidates appear here.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        These candidates submitted their form and await manual HR review — either held by
        position-level answer screening (interview tier) or collect-info only (details tier, 50–79%).
        Review the results and send the interview scheduling link when ready.
      </p>

      {flagged.map((c) => {
        const tier = candidateTier(c);
        const badge = FORM_SCREENING_BADGE[c.form_screening_status];
        const notes = c.form_screening_notes || [];
        return (
          <div key={c.id} className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-gray-800">{c.name}</span>
                  {/* Three branches, not two. candidateTier() returns null when
                      the score is null (a screening that failed), and the old
                      two-way ternary sent that through the else — rendering
                      "📋 Details · %", which claims a tier the candidate was
                      never given and an empty score as if it were a value. */}
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    tier === null ? 'bg-gray-100 text-gray-600'
                      : tier === 'interview' ? 'bg-green-100 text-green-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}>
                    {tier === null
                      ? '⚠️ Not scored'
                      : `${tier === 'interview' ? '🎯 Interview' : '📋 Details'} · ${c.screening_score}%`}
                  </span>
                  {badge && (
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
                      {badge.label}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {c.email || '—'}
                  {c.matched_position_title
                    ? <> · 📌 {c.matched_position_title}</>
                    : <> · 📌 <span className="italic">{NO_MATCH}</span></>}
                </p>
              </div>
              <button
                onClick={() => sendInvite(c.id)}
                disabled={!!sending[c.id] || !canOverride}
                title={canOverride ? '' : "🔒 You don't have permission"}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PaperAirplaneIcon className="w-4 h-4" />
                {sending[c.id] ? 'Sending…' : 'Send Schedule Invite'}
              </button>
            </div>

            {notes.length === 0 ? (
              <p className="px-5 py-4 text-sm text-gray-500">
                {c.form_screening_status === 'pending_hr_review'
                  ? 'No form questions are configured for this department. The form invite was not sent. Send the scheduling link manually when ready.'
                  : tier === 'details'
                    ? 'Details tier — no answer thresholds configured. Manual review only; form responses are in the Form Responses tab.'
                    : 'No threshold checks recorded. See the Form Responses tab for the full answers.'}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wide text-gray-500">
                      <th className="text-left px-4 py-2 font-semibold">Field</th>
                      <th className="text-left px-4 py-2 font-semibold">Answer</th>
                      <th className="text-left px-4 py-2 font-semibold">Threshold</th>
                      <th className="text-left px-4 py-2 font-semibold">Result</th>
                      <th className="text-left px-4 py-2 font-semibold">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notes.map((n, i) => {
                      const meta = FIELD_STATUS_META[n.status] || {};
                      return (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-700">{n.field}</td>
                          <td className="px-4 py-2 text-gray-600">{n.candidate_answer || '—'}</td>
                          <td className="px-4 py-2 text-gray-500">{n.threshold || '—'}</td>
                          <td className={`px-4 py-2 font-medium ${meta.color || ''}`}>
                            {meta.icon} {n.status}
                          </td>
                          <td className="px-4 py-2 text-gray-500">{n.note || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


/* ─────────────────────────── Form Responses ─────────────────────── */

/**
 * Form Responses.
 *
 * `search`, `page` and `selected` are PROPS, deliberately. They are held by
 * CvScreening because reload() unmounts this component — including via the
 * Refresh button in this tab's own header. See the note beside their
 * declarations at the top of the page component before moving them back.
 *
 * The in-flight flags below stay local for the opposite reason: they must NOT
 * survive an unmount, or a spinner outlives the request it was tracking.
 *
 * Filtering is client-side. GET /api/forms/submissions/enriched takes only
 * `department_id` — it has no limit/offset/q — and at 62 rows (all the answer
 * payloads together are under 3KB) there is nothing to gain from a round trip.
 * If this list ever reaches the thousands, the fix is those three params on the
 * endpoint, not a cleverer filter here.
 */
function FormResponsesTab({
  submissions, deptId, reload, title = 'Form Responses', queueMode = false,
  reviewFilter = 'all', setReviewFilter,
  onReviewed,
  search, setSearch, page, setPage, selected, setSelected,
}) {
  const { canModule } = useAuth();
  const canSchedule = canModule('hr_interview_scheduling', 'edit');
  const canReject = canModule('hr_cv_screening', 'edit');
  const [exporting, setExporting] = useState(false);
  const [sendingSchedule, setSendingSchedule] = useState(false);
  const [sendingReject, setSendingReject] = useState(false);
  const [sendingExam, setSendingExam] = useState(false);
  const [markingReviewed, setMarkingReviewed] = useState(false);
  const canReview = canModule('hr_cv_screening', 'edit');
  const canSendExam = canModule('hr_cv_screening', 'create');

  const PER_PAGE = 20;

  const query = search.trim().toLowerCase();

  // Name, email and position title. Case-insensitive substring; no debounce,
  // which at this size is not an optimisation worth the input lag it costs.
  const filtered = useMemo(() => {
    const rows = submissions.filter(s => queueMode || reviewFilter === 'all'
      || (reviewFilter === 'reviewed' ? !!s.reviewed_at : !s.reviewed_at));
    if (!query) return rows;
    return rows.filter((s) => (
      (s.candidate_name || '').toLowerCase().includes(query)
      || (s.candidate_email || '').toLowerCase().includes(query)
      || (s.position_title || '').toLowerCase().includes(query)
    ));
  }, [submissions, query, reviewFilter, queueMode]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));

  // Clamp rather than trust `page`. Typing a query while on page 3 leaves the
  // page number pointing past the end of a shorter result set, and slicing
  // there yields an empty list on a search that DID match — the failure this
  // is here to prevent. setSearch resets the page too, but that write lands a
  // render later; this makes the render itself correct in the meantime, and
  // also covers the shrink that happens when reload() drops a row.
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PER_PAGE;
  const visible = filtered.slice(start, start + PER_PAGE);

  const onSearchChange = (value) => {
    setSearch(value);
    setPage(1);          // a new query always starts at the top of its results
  };

  // Selection survives a filter that excludes it — see the notice above the
  // detail pane. Deliberately NOT cleared and NOT auto-moved: the two buttons
  // in that pane send real email to a real applicant, so silently changing who
  // is loaded there is the one behaviour that could cause a misdirected send.
  const selectedHidden = !!selected && !filtered.some((s) => s.id === selected.id);

  const markReviewed = async () => {
    if (!selected || selected.reviewed_at || markingReviewed) return;
    const submissionId = selected.id;
    setMarkingReviewed(true);
    try {
      const res = await formsAPI.markReviewed(submissionId);
      setSelected(previous => previous?.id === submissionId ? { ...previous, ...res.data } : previous);
      onReviewed(submissionId, res.data);
      toast.success('Response marked reviewed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not mark response reviewed');
    } finally {
      setMarkingReviewed(false);
    }
  };

  const sendSchedule = async () => {
    if (!selected || selected.assessment?.schedule_block_reason || sendingExam) return;
    setSendingSchedule(true);
    try {
      await interviewsAPI.sendScheduleLink(selected.candidate_id);
      toast.success(`Scheduling request processed for ${selected.candidate_name}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send scheduling link');
    } finally {
      setSendingSchedule(false);
    }
  };

  const sendReject = async () => {
    if (!selected) return;
    if (!window.confirm(`Send rejection email to ${selected.candidate_name}?`)) return;
    setSendingReject(true);
    try {
      await cvScreeningAPI.sendScreenedEmail(selected.candidate_id, 'rejection');
      toast.success(`Rejection email sent to ${selected.candidate_name}`);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send rejection email');
    } finally {
      setSendingReject(false);
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await formsAPI.exportCsv(deptId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'form_submissions.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold text-gray-800">{title}</h2>
          {submissions.length > 0 && (
            <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
              {submissions.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={reload} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700">
            <ArrowPathIcon className="w-3.5 h-3.5" /> Refresh
          </button>
          {!queueMode && submissions.length > 0 && (
            <button
              onClick={exportCsv}
              disabled={exporting}
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-60"
            >
              <ArrowDownTrayIcon className="w-3.5 h-3.5" />
              {exporting ? 'Exporting…' : 'Export CSV'}
            </button>
          )}
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200 text-gray-400">
          <ClipboardDocumentListIcon className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p className="font-medium">{queueMode ? 'No candidates in this queue' : 'No form submissions yet'}</p>
          <p className="text-sm mt-1">
            {queueMode ? 'Use Refresh to check for updates.' : 'Submitted forms will appear here.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* List */}
          <div className="lg:col-span-2 space-y-2">
            {!queueMode && (
              <div className="space-y-1">
                <label htmlFor="response-review-filter" className="text-xs font-medium text-gray-600">Review status</label>
                <select id="response-review-filter" aria-label="Response review status" value={reviewFilter}
                  onChange={e => { setReviewFilter(e.target.value); setPage(1); }}
                  className="w-full min-h-[44px] px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-300">
                  <option value="all">All responses</option>
                  <option value="unreviewed">Unreviewed ({submissions.filter(s => !s.reviewed_at).length})</option>
                  <option value="reviewed">Reviewed ({submissions.filter(s => !!s.reviewed_at).length})</option>
                </select>
              </div>
            )}
            <div className="relative">
              <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search name, email or position…"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
              />
            </div>

            {/* The count line never states a bare number that could be read as
                the grand total. With a filter active it says how many matched
                AND how many exist, because "Showing 1–12 of 12" beside a tab
                badge reading 62 is exactly the ambiguity this avoids. */}
            <p className="text-xs text-gray-500 px-0.5">
              {filtered.length === 0 ? (
                <>No matches — {submissions.length} total</>
              ) : query || (!queueMode && reviewFilter !== 'all') ? (
                <>
                  Showing {start + 1}–{Math.min(start + PER_PAGE, filtered.length)} of{' '}
                  {filtered.length} matching ({submissions.length} total)
                </>
              ) : (
                <>Showing {start + 1}–{Math.min(start + PER_PAGE, filtered.length)} of {filtered.length}</>
              )}
            </p>

            {filtered.length === 0 && (
              <div className="text-center py-10 bg-white rounded-xl border border-dashed border-gray-200">
                <p className="text-sm text-gray-500">
                  {query ? <>No responses match &lsquo;<span className="font-medium text-gray-700">{search.trim()}</span>&rsquo;</>
                    : 'No responses with this review status.'}
                </p>
                <button
                  onClick={() => { onSearchChange(''); if (!queueMode) setReviewFilter('all'); }}
                  className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  {!queueMode && reviewFilter !== 'all' ? 'Clear filters' : 'Clear search'}
                </button>
              </div>
            )}

            {visible.map((s) => (
              <button
                key={s.id}
                onClick={() => setSelected(s)}
                className={`w-full text-left p-4 rounded-xl border transition-colors ${
                  selected?.id === s.id
                    ? 'border-purple-300 bg-purple-50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="font-semibold text-gray-800 text-sm">{s.candidate_name}</p>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${s.reviewed_at
                    ? 'bg-green-50 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                    {s.reviewed_at ? 'Reviewed' : 'Unreviewed'}
                  </span>
                </div>
                <p className="text-xs font-medium text-indigo-700 mt-1">{stageLabels[s.workflow_stage] || s.workflow_stage}
                  {s.assessment && ` · ${s.assessment.status.replace('_', ' ')}`}
                  {['passed', 'failed'].includes(s.assessment?.status) && ` · ${s.assessment.pct}%`}</p>
                <p className="text-xs text-gray-500">{s.candidate_email}</p>
                {s.position_title && (
                  <p className="text-xs text-gray-400 mt-0.5">📌 {s.position_title}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  {new Date(s.submitted_at).toLocaleDateString('en-GB', {
                    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                  })}
                </p>
              </button>
            ))}

            {totalPages > 1 && (
              <div className="flex items-center justify-between gap-2 pt-1">
                <button
                  onClick={() => setPage(safePage - 1)}
                  disabled={safePage <= 1}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                <span className="text-xs text-gray-500">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(safePage + 1)}
                  disabled={safePage >= totalPages}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3">
            {/* The selected response is no longer in the filtered list.
                The pane KEEPS it rather than blanking or auto-selecting: the
                two buttons below send an interview link and a rejection email
                to a real applicant, so silently swapping who is loaded here is
                the one failure mode that misdirects a send.
                It NAMES the candidate rather than saying "not in results",
                because a generic label leaves it ambiguous who a click hits —
                which is the whole reason the notice exists. The buttons stay
                enabled: selecting someone, searching to check something else,
                then acting on the original is a legitimate flow. */}
            {selectedHidden && (
              <div className="mb-3 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                <ExclamationCircleIcon className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900">
                  <span className="font-semibold">{selected.candidate_name}</span>
                  {' '}— not in the current filtered list. Still shown below, and the
                  buttons here still act on them.
                </p>
              </div>
            )}
            {selected ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <div>
                    <h3 className="font-bold text-gray-900">{selected.candidate_name}</h3>
                    <p className="text-sm text-gray-500">{selected.candidate_email}</p>
                    {selected.position_title && (
                      <p className="text-xs text-gray-400 mt-0.5">Applied for: <span className="font-medium text-gray-600">{selected.position_title}</span></p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className="text-xs text-gray-400">
                      {new Date(selected.submitted_at).toLocaleString()}
                    </span>
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      <button
                        onClick={sendSchedule}
                        disabled={sendingSchedule || sendingReject || sendingExam || !canSchedule || !!selected.assessment?.schedule_block_reason}
                        title={selected.assessment?.schedule_block_reason || (canSchedule ? 'Send interview scheduling link' : "🔒 You don't have permission")}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-cyan-200 text-cyan-700 rounded-lg hover:bg-cyan-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <CalendarDaysIcon className="w-4 h-4" />
                        {sendingSchedule ? 'Sending…' : 'Send Interview Schedule'}
                      </button>
                      <button
                        onClick={sendReject}
                        disabled={sendingReject || sendingSchedule || sendingExam || !canReject}
                        title={canReject ? 'Send rejection email' : "🔒 You don't have permission"}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircleIcon className="w-4 h-4" />
                        {sendingReject ? 'Sending…' : 'Send Rejection Email'}
                      </button>
                    </div>
                  </div>
                </div>
                <hr className="mb-4" />

                <div className="mb-4 flex items-center justify-between gap-3 flex-wrap rounded-lg bg-gray-50 px-3 py-2">
                  <p className="text-sm text-gray-700" role="status" aria-atomic="true">
                    {selected.reviewed_at
                      ? `Reviewed by ${selected.reviewed_by_name || 'HR'} · ${new Date(selected.reviewed_at).toLocaleString()}`
                      : 'Unreviewed — HR has not marked this response reviewed.'}
                  </p>
                  {!selected.reviewed_at && (
                    <button onClick={markReviewed} disabled={markingReviewed || !canReview}
                      className="min-h-[44px] px-3 py-2 text-sm font-medium border border-green-200 text-green-800 rounded-lg hover:bg-green-50 disabled:opacity-50 focus:ring-2 focus:ring-green-300">
                      {markingReviewed ? 'Saving…' : 'Mark reviewed'}
                    </button>
                  )}
                </div>

                <RemoteAssessmentPanel key={selected.candidate_id}
                  candidateId={selected.candidate_id} candidateName={selected.candidate_name}
                  assessment={selected.assessment} canSend={canSendExam && !sendingReject && !sendingSchedule}
                  onBusy={setSendingExam}
                  onFailure={detail => { toast.error(detail); reload(); }}
                  onChange={assessment => {
                    const candidateId = selected.candidate_id;
                    setSelected(previous => previous?.candidate_id === candidateId ? { ...previous, assessment } : previous);
                    reload();
                  }} />

                {selected.answers.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">No answers recorded.</p>
                ) : (
                  <div className="space-y-4">
                    {selected.answers.map((a, idx) => (
                      <div key={a.question_id} className="space-y-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Q{idx + 1} — {a.question_text}
                        </p>
                        <div className="bg-gray-50 rounded-lg px-3 py-2 text-sm text-gray-800">
                          {Array.isArray(a.answer)
                            ? a.answer.length > 0
                              ? <ul className="list-disc list-inside space-y-0.5">{a.answer.map((v, i) => <li key={i}>{v}</li>)}</ul>
                              : <span className="text-gray-400 italic">No selection</span>
                            : String(a.answer) || <span className="text-gray-400 italic">No answer</span>
                          }
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full min-h-48 flex items-center justify-center text-gray-400 bg-white rounded-2xl border border-dashed border-gray-200">
                <p className="text-sm">← Select a submission to view answers</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


/* ─────────────────────────── Filter Chip ────────────────────────── */

// ── Non-response follow-ups ────────────────────────────────────────────────
//
// WHAT THIS ADDS OVER THE CANDIDATES TAB'S STATUS FILTER. Selecting
// "form_sent" there returns the same 50 rows this tab starts from, so the
// obvious later "simplification" is to delete this and tell people to use the
// filter. It is not the same list, and here is exactly what would be lost:
//
//   * THE AGE DIMENSION. The filter has no clock. This list is keyed on
//     form_sent_at / schedule_sent_at — whichever one the candidate's status
//     names — and 24h is the floor for appearing at all.
//   * THE BUCKETS. 41 of 55 rows are more than a week old and are effectively
//     dead; 2 are inside 48h and are the ones a call converts. The filter
//     shows those 43 as one undifferentiated block.
//   * SEVEN EXCLUSIONS. Candidates whose address is one of ours, who have no
//     matched position, or whose screening produced no score never received
//     something they could act on. Phoning them to ask why they did not reply
//     is asking about an email that could not work.
//   * FOUR WHO ALREADY BOOKED. Cancelling a booking reverts the candidate to
//     schedule_sent WITHOUT touching schedule_sent_at, so on status and clock
//     alone they read as days of silence. They answered.
//   * BOTH WAITING STATUSES AT ONCE. The status filter is single-value, so
//     form_sent and schedule_sent can only be viewed separately.
//   * SNOOZE. A candidate called an hour ago must not be on the list HR is
//     working down right now.
//   * THE CALL RECORD. The filter records nothing. Every action here writes a
//     candidate_followups row and an audit row.
//
// Rows come from GET /api/cv/followups and NOT from the `candidates` array the
// other tabs share. That is deliberate: every one of the exclusions above is
// computed server-side against send_policy and recipient_policy, and rebuilding
// them here would be a second copy of rules that exist in one place precisely
// so they cannot drift.
const BUCKET_META = {
  '24-48h': { label: '24–48 hours', color: 'bg-amber-100 text-amber-800',
              hint: 'Freshest — most likely to convert' },
  '2-7d':   { label: '2–7 days',    color: 'bg-orange-100 text-orange-800',
              hint: 'Going cold' },
  '7d+':    { label: 'Over 7 days', color: 'bg-gray-200 text-gray-700',
              hint: 'Largely dead — a long backlog, not a fresh queue' },
};
const BUCKET_ORDER = ['24-48h', '2-7d', '7d+'];

const WAITING_META = {
  form:     { label: 'Form',     color: 'bg-blue-100 text-blue-700' },
  schedule: { label: 'Schedule', color: 'bg-cyan-100 text-cyan-700' },
};

const OUTCOME_META = {
  called_will_respond: { label: 'Will respond', color: 'border-green-200 text-green-700 hover:bg-green-50' },
  no_answer:           { label: 'No answer',   color: 'border-amber-200 text-amber-700 hover:bg-amber-50' },
  closed:              { label: 'Close',       color: 'border-red-200 text-red-600 hover:bg-red-50' },
};

// A row on this list exists to be phoned, so it can never be anonymous.
// candidates.name is NOT NULL but accepts an empty string, and intake writes
// the placeholder "Pending Screening" when extraction found nothing — so a row
// with nothing usable in the name column is reachable, and it rendered as bare
// position-and-score with no identifier at all. Falls back to the address's
// local part, then to the id, which is at least something to search on.
function displayName(r) {
  const name = (r.name || '').trim();
  if (name) return name;
  const local = (r.email || '').split('@')[0].trim();
  if (local) return local;
  return `Candidate #${r.candidate_id}`;
}

function ageDays(iso) {
  return (Date.now() - new Date(iso).getTime()) / 86_400_000;
}

function fmtWaitingSince(iso) {
  const d = new Date(iso);
  const days = ageDays(iso);
  const rel = days < 2
    ? `${Math.floor(days * 24)}h ago`
    : `${Math.floor(days)} days ago`;
  return { absolute: d.toLocaleString(), relative: rel };
}

function FollowupsTab({ deptId, canEdit }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Whether the FIRST load has finished. The full-tab spinner is gated on this
  // and not on `loading` alone: showing it on a later refetch unmounts the
  // whole table, which collapses the scroll container and throws the reader
  // back to the top. On a six-screen backlog that happened after every single
  // call. See `act` for why an action no longer refetches at all.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // 7d+ starts collapsed. NOT the same as filtering it away: the heading, the
  // count and "largely dead" stay on screen, so the backlog is reported rather
  // than hidden — it just does not cost four screens of scrolling to get past.
  const [collapsed, setCollapsed] = useState({ '7d+': true });
  const [bucketFilter, setBucketFilter] = useState('all');
  const [waitingFilter, setWaitingFilter] = useState('all');
  const [callableOnly, setCallableOnly] = useState(false);
  const [busy, setBusy] = useState({});
  const [confirmClose, setConfirmClose] = useState(null);
  // No-answer had NO note field at all — the button called act() directly.
  // That is why every no-answer row in production has a NULL note: not
  // skipped, absent. Its own state rather than a shared one, so the close
  // dialog is untouched.
  const [confirmNoAnswer, setConfirmNoAnswer] = useState(null);
  const [note, setNote] = useState('');
  const [loadingPdf, setLoadingPdf] = useState({});
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, filename: null });
  // What just left the list and why. Without this the tab looks like it is
  // losing rows: an action makes the row vanish, which is correct — it is
  // snoozed or closed — but indistinguishable from a bug at a glance.
  const [recentlyActioned, setRecentlyActioned] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setRefreshing(true);
    try {
      const res = await cvAPI.listFollowups(deptId);
      setRows(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load follow-ups');
    } finally {
      setLoading(false);
      setRefreshing(false);
      setHasLoaded(true);
    }
  }, [deptId]);

  useEffect(() => { load(); }, [load]);

  // Same CV, new tab. Copied in shape from CandidatesTab::openCvInTab rather
  // than reinvented, and the three comments there are the reason why:
  // window.open must happen BEFORE the await or a popup blocker kills it, the
  // route needs a bearer token so a bare URL will not do, and the object URL is
  // released on a timer because the tab is still reading from it. Reloading
  // that tab after 60s blanks it; known, not a defect.
  const openCvInTab = async (candidateId) => {
    const tab = window.open('', '_blank');
    setLoadingPdf((p) => ({ ...p, [candidateId]: true }));
    try {
      const res = await cvAPI.getCv(candidateId);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      if (tab) {
        tab.location = blobUrl;
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
      } else {
        setPdfModal({ open: true, url: blobUrl, filename: 'CV Preview' });
      }
    } catch (err) {
      tab?.close();
      let msg = 'Could not load CV';
      try {
        const text = await err.response?.data?.text?.();
        if (text) msg = JSON.parse(text).detail || msg;
      } catch { /* fall back to the generic message */ }
      toast.error(msg);
    } finally {
      setLoadingPdf((p) => ({ ...p, [candidateId]: false }));
    }
  };

  const closePdf = () => {
    if (pdfModal.url) URL.revokeObjectURL(pdfModal.url);
    setPdfModal({ open: false, url: null, filename: null });
  };

  const act = async (row, outcome, noteText) => {
    setBusy((b) => ({ ...b, [row.candidate_id]: true }));
    try {
      const res = await cvAPI.recordFollowup(row.candidate_id, { outcome, note: noteText || null });
      const closed = outcome === 'closed';
      const back = new Date(Date.now() + 24 * 3600 * 1000);
      setRecentlyActioned((prev) => [
        {
          id: row.candidate_id,
          name: displayName(row),
          outcome,
          returnsAt: closed ? null : back,
        },
        ...prev.filter((r) => r.id !== row.candidate_id),
      ]);
      // Say where the row went. "Saved" would be true and useless — the visible
      // consequence is that it disappeared, so that is what the message is about.
      toast.success(
        closed
          ? `${displayName(row)} closed — they will receive nothing further`
          : `${displayName(row)} logged — back on this list after ${back.toLocaleString()}`,
      );
      setConfirmClose(null);
      setConfirmNoAnswer(null);
      setNote('');
      // DROP THE ROW LOCALLY. Deliberately NOT `await load()`.
      //
      // The server has already decided this candidate is gone: every outcome
      // either snoozes them for 24h or closes them, and both remove them from
      // the derived list. Refetching to be told that is a round trip to learn
      // something we caused — and it cost more than latency. load() sets
      // loading, the full-tab spinner replaced the table, the scroll container
      // collapsed to spinner height, and the reader was thrown back to the top
      // of a six-screen list after every call. Measured: scrollTop 2500 -> 0.
      //
      // Removing the one row keeps every other row, the scroll position and the
      // open filters exactly where they were. The list is derived, so anything
      // else that changed underneath arrives on the next Refresh or revisit —
      // which was already true, since nothing here polls.
      setRows((prev) => prev.filter((x) => x.candidate_id !== row.candidate_id));
      return res;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the follow-up');
    } finally {
      setBusy((b) => ({ ...b, [row.candidate_id]: false }));
    }
  };

  const counts = useMemo(() => {
    const c = { total: rows.length, noPhone: 0 };
    BUCKET_ORDER.forEach((b) => { c[b] = 0; });
    rows.forEach((r) => {
      c[r.age_bucket] = (c[r.age_bucket] || 0) + 1;
      if (!r.has_phone) c.noPhone += 1;
    });
    return c;
  }, [rows]);

  const visible = useMemo(() => rows.filter((r) => {
    if (bucketFilter !== 'all' && r.age_bucket !== bucketFilter) return false;
    if (waitingFilter !== 'all' && r.waiting_on !== waitingFilter) return false;
    if (callableOnly && !r.has_phone) return false;
    return true;
  }), [rows, bucketFilter, waitingFilter, callableOnly]);

  // Grouped under bucket headings, each group still newest-first. The server
  // already sorts newest-first and that order is preserved inside every group —
  // a 54-day-old form is dead and a 3-day-old one is live, so the freshest row
  // must never be at the bottom.
  const grouped = useMemo(() => BUCKET_ORDER
    .map((b) => [b, visible.filter((r) => r.age_bucket === b)])
    .filter(([, list]) => list.length > 0), [visible]);

  // FIRST load only. Gating on `loading` alone put this in front of the table
  // on every refetch, unmounting it and resetting the scroll — see `hasLoaded`.
  // A manual Refresh reports itself on its own button instead.
  if (loading && !hasLoaded) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-semibold text-gray-800">No response after 24 hours</h3>
            <p className="text-xs text-gray-500 mt-0.5 max-w-2xl">
              Candidates sent a form invite or a scheduling link who have not replied.
              Anyone who submitted, booked, or never received a working link is already
              excluded. Calling is the only action here.
            </p>
          </div>
          <button
            onClick={load}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <FilterChip label={`All (${counts.total})`} active={bucketFilter === 'all'}
                      onClick={() => setBucketFilter('all')}
                      activeClass="bg-gray-800 text-white border-gray-800" />
          {BUCKET_ORDER.map((b) => (
            <FilterChip
              key={b}
              label={`${BUCKET_META[b].label} (${counts[b] || 0})`}
              active={bucketFilter === b}
              onClick={() => setBucketFilter(bucketFilter === b ? 'all' : b)}
              activeClass={`${BUCKET_META[b].color} border-transparent`}
            />
          ))}
          <span className="w-px h-5 bg-gray-200 mx-1" />
          {['form', 'schedule'].map((w) => (
            <FilterChip
              key={w}
              label={`Waiting on ${WAITING_META[w].label.toLowerCase()}`}
              active={waitingFilter === w}
              onClick={() => setWaitingFilter(waitingFilter === w ? 'all' : w)}
              activeClass={`${WAITING_META[w].color} border-transparent`}
            />
          ))}
          {counts.noPhone > 0 && (
            <>
              <span className="w-px h-5 bg-gray-200 mx-1" />
              <FilterChip
                label={`Hide ${counts.noPhone} with no phone`}
                active={callableOnly}
                onClick={() => setCallableOnly(!callableOnly)}
                activeClass="bg-red-100 text-red-700 border-transparent"
              />
            </>
          )}
        </div>

        {counts.noPhone > 0 && (
          <p className="text-xs text-red-600 flex items-center gap-1.5">
            <ExclamationCircleIcon className="w-4 h-4 shrink-0" />
            {counts.noPhone} of {counts.total} have no phone number on file and cannot be
            called. They are marked in the list.
          </p>
        )}
      </div>

      {/* STICKY, and that is a consequence of fixing the scroll reset rather
          than a style choice. While every action bounced the reader to the top,
          this banner sat at the top and was therefore always in view. Now that
          the scroll position survives, acting on a row four screens down would
          leave the explanation for the vanished row somewhere off-screen above
          — which is the confusion it was written to prevent. The toast is
          transient; this is what is still there ten seconds later. */}
      {recentlyActioned.length > 0 && (
        <div className="sticky top-0 z-20 bg-blue-50 border border-blue-200 rounded-xl p-3 shadow-sm">
          <p className="text-xs font-medium text-blue-900 mb-1">
            Rows you just acted on have left this list. That is expected:
          </p>
          <ul className="text-xs text-blue-800 space-y-0.5">
            {recentlyActioned.map((r) => (
              <li key={r.id}>
                <span className="font-medium">{r.name}</span>{' '}
                {r.outcome === 'closed'
                  ? '— closed as no response. Will not come back.'
                  : `— hidden for 24 hours, back after ${r.returnsAt.toLocaleString()}.`}
              </li>
            ))}
          </ul>
          <button onClick={() => setRecentlyActioned([])}
                  className="text-xs text-blue-700 underline mt-1.5">
            Dismiss
          </button>
        </div>
      )}

      {visible.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-10 text-center">
          <CheckCircleIcon className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="text-sm text-gray-600">
            {rows.length === 0
              ? 'Nobody is waiting on a reply right now.'
              : 'No rows match these filters.'}
          </p>
        </div>
      ) : grouped.map(([bucket, list]) => (
        <div key={bucket} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          {/* The heading is the toggle. Count and hint render whether the group
              is open or shut, so a collapsed 7d+ still says "48 candidates ·
              largely dead" — the backlog is reported, only the four screens of
              rows are folded away. That is the opposite of the filter chips
              above, which remove rows from the view entirely. */}
          <button
            onClick={() => setCollapsed((c) => ({ ...c, [bucket]: !c[bucket] }))}
            className={`w-full px-4 py-2 flex items-center gap-2 text-left ${BUCKET_META[bucket].color}`}
          >
            <ChevronRightIcon
              className={`w-4 h-4 shrink-0 transition-transform ${collapsed[bucket] ? '' : 'rotate-90'}`}
            />
            <span className="text-sm font-semibold">{BUCKET_META[bucket].label}</span>
            <span className="text-xs opacity-80">
              {list.length} {list.length === 1 ? 'candidate' : 'candidates'} · {BUCKET_META[bucket].hint}
            </span>
            <span className="ml-auto text-xs opacity-70">
              {collapsed[bucket] ? 'Show' : 'Hide'}
            </span>
          </button>
          <div className={`overflow-x-auto ${collapsed[bucket] ? 'hidden' : ''}`}>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Candidate</th>
                  <th className="px-4 py-2 text-left font-medium">Waiting on</th>
                  <th className="px-4 py-2 text-left font-medium">Sent</th>
                  <th className="px-4 py-2 text-left font-medium">Phone</th>
                  <th className="px-4 py-2 text-left font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {list.map((r) => {
                  const when = fmtWaitingSince(r.waiting_since);
                  // No row tint for a missing phone. It was red-50 at 40%
                  // opacity — measurably almost white, contributing nothing
                  // while looking like it carried meaning. The pill in the
                  // phone cell does the work.
                  return (
                    <tr key={r.candidate_id}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{displayName(r)}</div>
                        {/* One line, truncated, full text on hover. The title
                            repeats identically down the column — one production
                            position title is 77 characters — so it carries no
                            per-row information while wrapping to a second line. */}
                        <div className="text-xs text-gray-500 truncate max-w-[22rem]"
                             title={`${r.position_title || 'No position'}${r.screening_score != null ? ` · score ${r.screening_score}` : ''}`}>
                          {r.position_title || 'No position'}
                          {r.screening_score != null && ` · score ${r.screening_score}`}
                        </div>
                        {r.followup_count > 0 && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            {r.followup_count} previous
                            {r.followup_count === 1 ? ' call' : ' calls'}
                            {r.last_followup && ` · last: ${OUTCOME_META[r.last_followup.outcome]?.label || r.last_followup.outcome}`}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${WAITING_META[r.waiting_on]?.color || ''}`}>
                          {WAITING_META[r.waiting_on]?.label || r.waiting_on}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-gray-700">{when.relative}</div>
                        <div className="text-xs text-gray-400">{when.absolute}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        {/* A row that cannot be called is marked HERE, in the list.
                            The only action on this screen is "phone them", so
                            finding out on click that there is no number wastes the
                            click and, on a 55-row backlog, does it repeatedly. */}
                        {r.has_phone ? (
                          <a href={`tel:${r.phone}`} className="text-blue-600 hover:underline">
                            {r.phone}
                          </a>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <ExclamationCircleIcon className="w-3.5 h-3.5" />
                            No phone number
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <button
                            onClick={() => openCvInTab(r.candidate_id)}
                            disabled={!!loadingPdf[r.candidate_id]}
                            className="flex items-center gap-1 text-xs px-2.5 py-1 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          >
                            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                            {loadingPdf[r.candidate_id] ? '…' : 'Open CV'}
                          </button>
                          {['called_will_respond', 'no_answer'].map((o) => (
                            <button
                              key={o}
                              // called_will_respond stays a direct action: the
                              // outcome says everything and there is nothing to
                              // record. no_answer opens a dialog because "what
                              // happened" is exactly what the report needs.
                              onClick={() => (o === 'no_answer'
                                ? (setNote(''), setConfirmNoAnswer(r))
                                : act(r, o))}
                              disabled={!!busy[r.candidate_id] || !canEdit}
                              title={canEdit ? undefined : "🔒 You don't have permission"}
                              className={`text-xs px-2.5 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${OUTCOME_META[o].color}`}
                            >
                              {OUTCOME_META[o].label}
                            </button>
                          ))}
                          <button
                            onClick={() => { setNote(''); setConfirmClose(r); }}
                            disabled={!!busy[r.candidate_id] || !canEdit}
                            title={canEdit ? undefined : "🔒 You don't have permission"}
                            className={`text-xs px-2.5 py-1 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${OUTCOME_META.closed.color}`}
                          >
                            {OUTCOME_META.closed.label}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* No-answer note. A separate small dialog rather than a shared one, so
          the close dialog above is untouched. Optional on purpose: a required
          field gets "x" typed into it, and "x" in the HR report is worse than
          an empty reason because it looks like one. */}
      <Modal open={!!confirmNoAnswer} onClose={() => setConfirmNoAnswer(null)}
             title="No answer — what happened?" size="md">
        {confirmNoAnswer && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Logging a no-answer call for{' '}
              <span className="font-medium">{displayName(confirmNoAnswer)}</span>. They
              stay on this list and come back in 24 hours.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                What happened on the call?{' '}
                <span className="text-gray-400">(optional)</span>
              </label>
              <p className="text-xs text-gray-500 mb-1.5">
                This appears in the HR report against this candidate. Without it the
                report can only say &quot;no answer&quot;.
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. phone switched off, number unobtainable, wrong number"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmNoAnswer(null)}
                      className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => act(confirmNoAnswer, 'no_answer', note)}
                disabled={!!busy[confirmNoAnswer.candidate_id]}
                className="px-4 py-2 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {busy[confirmNoAnswer.candidate_id] ? 'Saving…' : 'Log no answer'}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={!!confirmClose} onClose={() => setConfirmClose(null)}
             title="Close as no response?" size="md">
        {confirmClose && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              <span className="font-medium">{displayName(confirmClose)}</span> will be marked
              <span className="font-medium"> no response</span> and removed from this list
              permanently.
            </p>
            {/* Stated, not implied. Closing changes what the system will do to this
                person for good, and HR should not have to learn that from watching
                buttons disappear afterwards. Both halves are true of the backend:
                the close sets status = no_response, and send_policy refuses every
                recruitment send for that status. */}
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900 space-y-1.5">
              <p className="font-medium">What this does:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Sends the candidate <span className="font-medium">nothing</span>. They are not told.</li>
                <li>
                  Stops <span className="font-medium">all future sends</span> to them —
                  tier emails, form invites and scheduling links are all refused
                  from now on.
                </li>
                <li>Cannot be undone from this screen.</li>
              </ul>
              <p className="pt-1">
                Their existing form and scheduling links keep working, so if they do
                reply on their own they come back into the process normally.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Why are you closing this candidate?{' '}
                <span className="text-gray-400">(optional)</span>
              </label>
              {/* Says where the text GOES. The field already existed and was
                  skipped on every close in production — an unlabelled optional
                  box reads as a private scratchpad, not as something a manager
                  will read. */}
              <p className="text-xs text-gray-500 mb-1.5">
                This appears in the HR report as the reason this candidate was closed.
                Left blank, the report can only say &quot;closed — no response&quot;.
              </p>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                placeholder="e.g. four attempts over two weeks, no answer"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmClose(null)}
                      className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => act(confirmClose, 'closed', note)}
                disabled={!!busy[confirmClose.candidate_id]}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {/* The button describes what the click actually does. Nothing is
                    validated and nothing is blocked — leaving the note empty is
                    a legitimate choice, and this only makes it a stated one
                    rather than an oversight. Close only: no-answer recurs daily
                    and the same nudge there would become noise within a week. */}
                {busy[confirmClose.candidate_id]
                  ? 'Closing…'
                  : (note.trim() ? 'Close as no response' : 'Close without a reason')}
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal open={pdfModal.open} onClose={closePdf} title={pdfModal.filename || 'CV'} size="xl">
        {pdfModal.url && (
          <iframe title="CV preview" src={pdfModal.url} className="w-full h-[70vh] rounded-lg border border-gray-100" />
        )}
      </Modal>
    </div>
  );
}


function FilterChip({ label, active, onClick, activeClass }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? activeClass
          : 'border-gray-200 text-gray-600 bg-white hover:bg-gray-50'
      }`}
    >
      {label}
    </button>
  );
}
